import {createStore} from "vuex";
import {BigNumber, ethers} from "ethers";
import axios from "axios";
import WalletAdapter from "src/walletAdapter";
import {toRaw} from "vue";
import {
  createInjectedWeb3Provider,
  extractMetamaskErrorMessage,
  getPreferredProviderEthers,
  getProvider,
  getURLFromPieces,
} from "src/shared";
import TransportWebUSB from "@ledgerhq/hw-transport-webusb";
import Eth from "@ledgerhq/hw-app-eth";
import {Buffer} from "buffer";
import {state} from "../state";
import {getters} from "../getters";
import {mutations} from "../mutations";
//import { listen } from "@ledgerhq/logs";
import {
  AGROFORESTRY_PROJECT_ENDPOINT,
  CHAIN_CONFIG,
  DECIMAL_UNITS,
  ACTIVE_CHAIN,
  OFFERING_STATES,
  PENDING_REQUEST_MESSAGE,
  TESTING_ENV,
  TOKEN_DECIMALS,
} from "src/constants";
import networkConfig from "src/networks";
import { ABI_REGISTRY } from "src/contracts/abiRegistry";
import { hasMeaningfulValue } from "src/utils/valueChecks";
import {
  clearAuthenticatedWalletSession,
  getStoredMetaKeepSession,
  getPreferredProvider,
  toOnChainYear,
} from "src/utils";
import { getCarbonTimelineHorizon } from "src/utils/projectData";
import { normalizeSpeciesKey } from "src/utils/formatters";
import { readInjectedWalletChainId } from "src/utils/injectedWalletAuthority";
import { backendActions } from "src/store/backend";

const BRAZIL_CREDIT_ABI = ABI_REGISTRY.BrazilCarbonCreditUnified.abi;
const BRAZIL_AGRO_ABI = ABI_REGISTRY.AgroforestryProject.abi;
const BRAZIL_FACTORY_ABI = ABI_REGISTRY.AgroforestryProjectFactory.abi;
const AGRO_FACTORY_ACTIVE_ABI = [
  "function createAgroforestryProject(address stableCoin,address farmer,address auditor,address usdcTreasury,uint8 maxProjectYear,uint8 speciesCount,uint8[] yearStageCounts,string tokenUri,uint64[] stageCosts,uint64[] stagePrices,uint128[][] predictedRevenues,uint64[][] claimTimestamps) returns (address,address)",
  "event ProjectCreated(address indexed project,address indexed token,uint32 indexed projectId,string projectType,address creator)",
];
const CARBON_INVESTMENT_TOKEN_ABI =
  ABI_REGISTRY.CarbonCreditInvestmentToken.abi;
const CARBON_OFFERING_ABI = ABI_REGISTRY.CarbonCreditPrivateOffering.abi;
const USDC_ABI = ABI_REGISTRY.USDC.abi;
const SECONDS_PER_YEAR = 365 * 24 * 60 * 60;
const STAGE_VALUE_MASK = BigNumber.from((1n << 56n) - 1n);
const LOCAL_CHAIN_IDS = new Set(["31337", "31338"]);
const LOCAL_FACTORY_CREATE_GAS_LIMIT = BigNumber.from("5000000");
const injectedProviderListeners = new WeakMap();

function localFactoryTxOptions(networkId, gasLimit) {
  return LOCAL_CHAIN_IDS.has(String(networkId)) ? { gasLimit } : {};
}

export const decodePackedStage = (packed) => {
  const value = BigNumber.from(packed ?? 0);
  const cost = value.and(STAGE_VALUE_MASK);
  const price = value.shr(56).and(STAGE_VALUE_MASK);
  const raised = value.shr(112).and(STAGE_VALUE_MASK);
  const raisedAfterCommission = value.shr(168).and(STAGE_VALUE_MASK);
  const completed = value.shr(224).and(1).eq(1);
  const signed = value.shr(225).and(1).eq(1);
  const audited = value.shr(226).and(1).eq(1);
  const withdrawn = value.shr(227).and(1).eq(1);
  return {
    cost,
    price,
    raised,
    raisedAfterCommission,
    completed,
    signed,
    audited,
    withdrawn,
  };
};

const ERC1155_ABI = [
  {
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "uint256", name: "id", type: "uint256" },
    ],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "address", name: "operator", type: "address" },
    ],
    name: "isApprovedForAll",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "operator", type: "address" },
      { internalType: "bool", name: "approved", type: "bool" },
    ],
    name: "setApprovalForAll",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
];

const AGRO_PROJECT_INVEST_ABI = [
  {
    inputs: [{ internalType: "uint64", name: "amount", type: "uint64" }],
    name: "invest",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
];

const NETWORK_SWITCH_REJECTED_MESSAGE =
  "Network switch request was rejected.";
const getActiveNetwork = () =>
  Object.values(networkConfig).find(
    (network) => parseInt(network.chainId) === parseInt(ACTIVE_CHAIN.chainId)
  );
const getNetworkSwitchRequiredMessage = (network = getActiveNetwork()) => {
  const chainId = String(network?.chainId ?? ACTIVE_CHAIN.chainId);
  const networkName =
    network?.name || ACTIVE_CHAIN.name || "the required network";
  const switchInstruction = `Open MetaMask and switch to ${networkName} (chain ID ${chainId}).`;
  if (LOCAL_CHAIN_IDS.has(chainId) && network?.rpcUrl) {
    return `${switchInstruction} If it is already selected, set its RPC URL to ${network.rpcUrl}, then try again.`;
  }
  return `${switchInstruction} Then try again.`;
};
const getStaleRpcNetworkMessage = (error, network) => {
  const match = String(error?.message || "").match(
    /same RPC endpoint as existing network for chain (0x[0-9a-f]+)(?: \('([^']+)'\))?/i
  );
  if (!match) {
    return null;
  }
  const existingChainId = Number.parseInt(match[1], 16);
  if (!Number.isSafeInteger(existingChainId)) {
    return null;
  }
  const existingNetworkName = match[2] || "another custom network";
  return `MetaMask already uses ${network.rpcUrl} for ${existingNetworkName} (chain ID ${existingChainId}), but this app requires chain ID ${network.chainId}. Delete the stale chain ${existingChainId} custom network in MetaMask, then try again.`;
};
const METAMASK_LOCKED_MESSAGE =
  "MetaMask is locked or another wallet extension is active. Please unlock MetaMask and disable other wallet extensions.";
const NO_ACCOUNTS_MESSAGE = "No accounts returned from provider.";
const INVALID_ACCOUNT_MESSAGE =
  "Invalid account address returned from provider.";
const DEFAULT_AGRO_MINT_ERROR_MESSAGE =
  "Project deployment failed. Please try submitting again.";
const MISSING_PROJECT_ADDRESS_MESSAGE =
  "Project deployment finished unexpectedly and the app could not confirm the new project address.";
const MISSING_TOKEN_ADDRESS_MESSAGE =
  "Project deployment finished unexpectedly and the app could not confirm the new investment token address.";
const WALLET_RECONNECT_REQUIRED_MESSAGE =
  "Wallet connection was lost. Reconnect your wallet and try again.";
const CARBON_V1_UNSUPPORTED_MESSAGE =
  "Legacy Carbon runtime operations are disabled. Use the current Carbon flow.";

const normalizeAgroMintErrorMessage = (detail) => {
  const text = String(detail || "").trim();
  if (!text) {
    return DEFAULT_AGRO_MINT_ERROR_MESSAGE;
  }
  const lower = text.toLowerCase();
  if (lower.includes("user rejected")) {
    return "Transaction was rejected in the wallet.";
  }
  if (lower.includes("insufficient funds")) {
    return text;
  }
  if (lower.includes("internal json-rpc error")) {
    return DEFAULT_AGRO_MINT_ERROR_MESSAGE;
  }
  if (lower.includes("projectcreated event not found")) {
    return MISSING_PROJECT_ADDRESS_MESSAGE;
  }
  if (lower.includes("investment token address")) {
    return MISSING_TOKEN_ADDRESS_MESSAGE;
  }
  return text;
};

const ensureWalletAdapter = async ({ state, dispatch }) => {
  let adapter = toRaw(state.adapter);
  if (adapter?.executeMethod) {
    return adapter;
  }

  const accountType = state.currentAccount?.type;
  const canReconnectMetaMask =
    accountType === "MetaMask" ||
    (!accountType && !!state.currentAccount?.address);

  if (canReconnectMetaMask) {
    const walletProvider = await getPreferredProvider();
    if (walletProvider) {
      await dispatch("connectWallet", {
        walletProvider,
        walletId: state.currentAccount?.walletId,
      });
      adapter = toRaw(state.adapter);
    }
  }

  if (!adapter?.executeMethod) {
    throw new Error(WALLET_RECONNECT_REQUIRED_MESSAGE);
  }

  return adapter;
};

const extractProjectCreatedAddresses = (parsedLog) => {
  const projectAddress = parsedLog?.args?.project;
  const tokenAddress = parsedLog?.args?.token;
  if (!projectAddress) {
    throw new Error("ProjectCreated event not found in transaction receipt.");
  }
  if (!tokenAddress) {
    throw new Error("ProjectCreated event is missing the investment token address.");
  }
  return {
    projectAddress,
    tokenAddress,
  };
};

const unsupportedCarbonRuntime = (commit) => {
  if (commit) {
    commit("setWalletErrorMessage", CARBON_V1_UNSUPPORTED_MESSAGE);
  }
  throw new Error(CARBON_V1_UNSUPPORTED_MESSAGE);
};

const AGRO_TOKEN_ABI = ABI_REGISTRY.AgroforestryInvestmentToken.abi;

const AGRO_TOKEN_ID_SHIFT_YEAR = 248;
const AGRO_TOKEN_ID_SHIFT_SPECIES = 244;
const AGRO_TOKEN_ID_SHIFT_STAGE = 240;
const AGRO_TOKEN_ID_SHIFT_TIMESTAMP = 176;

const AGRO_UNIFIED_CACHE_TTL_MS = 15000;
const AGRO_UNIFIED_META_CACHE = new Map();
const AGRO_UNIFIED_BALANCE_CACHE = new Map();

const encodeAgroTokenId = (year, speciesId, stageIdx, claimTimestamp) =>
  BigNumber.from(year)
    .shl(AGRO_TOKEN_ID_SHIFT_YEAR)
    .or(BigNumber.from(speciesId).shl(AGRO_TOKEN_ID_SHIFT_SPECIES))
    .or(BigNumber.from(stageIdx).shl(AGRO_TOKEN_ID_SHIFT_STAGE))
    .or(BigNumber.from(claimTimestamp).shl(AGRO_TOKEN_ID_SHIFT_TIMESTAMP));

const resolveUnifiedCreditSaleToken = async ({
  projectAddress,
  year,
  provider,
}) => {
  const projectAbi = BRAZIL_CREDIT_ABI;
  const projectContract = new ethers.Contract(
    projectAddress,
    projectAbi,
    provider
  );
  const [investmentTokenAddress, stageIdxBn, yearConfig] = await Promise.all([
    projectContract.investmentToken(),
    projectContract.currentStageIndex(),
    projectContract.getYearConfig(year),
  ]);
  const claimTimestamp = yearConfig.claimTimestamp ?? yearConfig[3];
  const investmentAbi = CARBON_INVESTMENT_TOKEN_ABI;
  const investmentToken = new ethers.Contract(
    investmentTokenAddress,
    investmentAbi,
    provider
  );
  const tokenId = await investmentToken.encodeTokenId(
    year,
    Number(stageIdxBn),
    claimTimestamp
  );
  return { tokenId, investmentTokenAddress };
};

const resolveClaimedCarbonCreditSaleToken = async ({
  projectAddress,
  year,
  provider,
}) => {
  const projectContract = new ethers.Contract(
    projectAddress,
    BRAZIL_CREDIT_ABI,
    provider
  );
  const investmentTokenAddress = await projectContract.investmentToken();
  const investmentToken = new ethers.Contract(
    investmentTokenAddress,
    CARBON_INVESTMENT_TOKEN_ABI,
    provider
  );
  const claimState = await investmentToken.getYearClaimState(year);
  const creditTokenAddress = claimState.creditToken;
  const tokenId = claimState.creditTokenId;
  if (!creditTokenAddress || creditTokenAddress === ethers.constants.AddressZero) {
    throw new Error(`Carbon credit token is not configured for year ${year}.`);
  }
  return {
    tokenId,
    creditTokenAddress,
  };
};

const isDirectCreditListing = (project) => project?.project_type === "listed_credits";

const resolveDirectListedCreditSaleToken = async ({ project, year }) => {
  const rows = project?.direct_listing?.issued_rows;
  if (!Array.isArray(rows)) {
    throw new Error("Direct credit listing rows are missing.");
  }
  const row = rows.find((entry) => Number(entry.year) === Number(year));
  if (!row) {
    throw new Error(`Direct credit listing row is missing for year ${year}.`);
  }
  if (row.token_id === undefined || row.token_id === null) {
    throw new Error(`Direct credit listing token id is missing for year ${year}.`);
  }
  if (!project.carbon_credit_address) {
    throw new Error("Direct credit token address is missing.");
  }
  return {
    tokenId: BigNumber.from(String(row.token_id)),
    creditTokenAddress: project.carbon_credit_address,
  };
};

const resolveCarbonSaleToken = async ({ saleAsset, ...args }) => {
  if (saleAsset === "direct-credit") {
    return resolveDirectListedCreditSaleToken(args);
  }
  if (saleAsset === "claimed-credit") {
    return resolveClaimedCarbonCreditSaleToken(args);
  }
  if (saleAsset === "investment-token") {
    return resolveUnifiedCreditSaleToken(args);
  }
  throw new Error("Carbon sale asset type is required.");
};

const listCreditTokenForSaleWithYear = async ({
  adapter,
  accountAddress,
  creditTokenAddress,
  saleAddress,
  tokenId,
  amountUnits,
  listingPriceUnits,
  year,
}) => {
  if (!accountAddress) {
    throw new Error("Wallet is not connected.");
  }
  const creditToken = new ethers.Contract(
    creditTokenAddress,
    ERC1155_ABI,
    adapter.signer
  );
  const approved = await creditToken.isApprovedForAll(accountAddress, saleAddress);
  if (!approved) {
    await adapter.executeMethod(
      creditTokenAddress,
      ERC1155_ABI,
      "setApprovalForAll",
      [saleAddress, true]
    );
  }
  const { response } = await adapter.executeMethod(
    saleAddress,
    ABI_REGISTRY.CarbonCreditPublicSale.abi,
    "listForSaleWithYear",
    [
      BigNumber.from(String(tokenId)),
      BigNumber.from(String(amountUnits)),
      BigNumber.from(String(listingPriceUnits)),
      Number(year),
    ]
  );
  return response;
};

const readAgroCache = (cache, key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > AGRO_UNIFIED_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const writeAgroCache = (cache, key, value) => {
  cache.set(key, { ts: Date.now(), value });
};

const invalidateAgroBalanceCache = ({ projectAddress, tokenAddress, account }) => {
  if (!account) return;
  for (const key of AGRO_UNIFIED_BALANCE_CACHE.keys()) {
    const [proj, token, acct] = key.split("|");
    if (projectAddress && proj !== projectAddress) continue;
    if (tokenAddress && token !== tokenAddress) continue;
    if (acct !== account) continue;
    AGRO_UNIFIED_BALANCE_CACHE.delete(key);
  }
};

// const invalidateAgroMetaCache = (projectAddress) => {
//   if (projectAddress) {
//     AGRO_UNIFIED_META_CACHE.delete(projectAddress);
//   }
// };

const buildUnifiedAgroTokenMeta = async (projectContract) => {
  const [maxYearBn, speciesCountBn] = await Promise.all([
    projectContract.maxProjectYear(),
    projectContract.speciesCount(),
  ]);
  const maxYear = Number(maxYearBn);
  const speciesCount = Number(speciesCountBn);
  const meta = [];

  // Collect claimTimestamps per (claimYear, speciesId)
  const claimTsByYearSpecies = {};
  for (let claimYear = 1; claimYear <= maxYear; claimYear++) {
    for (let speciesId = 0; speciesId < speciesCount; speciesId++) {
      const config = await projectContract.getSpeciesConfig(
        claimYear,
        speciesId
      );
      const configured = config[4];
      if (!configured) continue;
      const claimTimestamp = BigNumber.from(config[3]);
      if (!claimTsByYearSpecies[claimYear])
        claimTsByYearSpecies[claimYear] = {};
      claimTsByYearSpecies[claimYear][speciesId] = claimTimestamp;
    }
  }

  // Enumerate all (investYear, speciesId, stageIdx, claimYear) combos
  for (let investYear = 1; investYear <= maxYear; investYear++) {
    const stageCount = Number(await projectContract.stageCountForYear(investYear));
    for (let speciesId = 0; speciesId < speciesCount; speciesId++) {
      for (let stageIdx = 0; stageIdx < stageCount; stageIdx++) {
        for (let claimYear = 1; claimYear <= maxYear; claimYear++) {
          const claimTimestamp =
            claimTsByYearSpecies[claimYear]?.[speciesId];
          if (!claimTimestamp) continue;
          const tokenId = encodeAgroTokenId(
            investYear,
            speciesId,
            stageIdx,
            claimTimestamp
          );
          meta.push({
            year: investYear,
            speciesId,
            stageIdx,
            claimYear,
            claimTimestamp,
            tokenId: tokenId.toString(),
          });
        }
      }
    }
  }

  return meta;
};

const getUnifiedAgroTokenMeta = async (
  projectContract,
  projectAddress,
  forceRefresh = false
) => {
  const key = projectAddress || projectContract.address;
  if (!forceRefresh) {
    const cached = readAgroCache(AGRO_UNIFIED_META_CACHE, key);
    if (cached) return cached;
  }
  const meta = await buildUnifiedAgroTokenMeta(projectContract);
  writeAgroCache(AGRO_UNIFIED_META_CACHE, key, meta);
  return meta;
};

const fetchAgroBalancesBatch = async (
  tokenContract,
  account,
  tokenMeta,
  chunkSize = 200
) => {
  if (!tokenMeta.length) return [];
  if (!account) return [];
  const results = [];
  for (let i = 0; i < tokenMeta.length; i += chunkSize) {
    const chunk = tokenMeta.slice(i, i + chunkSize);
    const accounts = Array(chunk.length).fill(account);
    const ids = chunk.map((entry) => entry.tokenId);
    try {
      const balances = await tokenContract.balanceOfBatch(accounts, ids.map(it => it.toString()));
      for (let j = 0; j < chunk.length; j++) {
        results.push({ ...chunk[j], balance: balances[j] });
      }
    } catch (error) {
      for (const entry of chunk) {
        const balance = await tokenContract.balanceOf(account, entry.tokenId);
        results.push({ ...entry, balance });
      }
    }
  }
  return results;
};

const getUnifiedAgroBalances = async ({
  projectContract,
  tokenContract,
  account,
  projectAddress,
  tokenAddress,
  forceRefresh = false,
}) => {
  const meta = await getUnifiedAgroTokenMeta(
    projectContract,
    projectAddress,
    forceRefresh
  );
  if (!meta.length) return [];
  const cacheKey = `${projectAddress}|${tokenAddress}|${account}`;
  if (!forceRefresh) {
    const cached = readAgroCache(AGRO_UNIFIED_BALANCE_CACHE, cacheKey);
    if (cached) return cached;
  }
  const balances = await fetchAgroBalancesBatch(
    tokenContract,
    account,
    meta
  );
  writeAgroCache(AGRO_UNIFIED_BALANCE_CACHE, cacheKey, balances);
  return balances;
};

const extractSpeciesName = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return null;
  const candidate =
    item.name ?? item.label ?? item.title ?? item.species ?? item.value;
  return typeof candidate === "string" ? candidate.trim() : null;
};

const normalizeSpeciesList = (speciesList) => {
  if (!Array.isArray(speciesList)) {
    return [];
  }
  return speciesList.map(extractSpeciesName).filter((item) => item);
};

const resolveSpeciesId = (species, speciesList = []) => {
  if (!hasMeaningfulValue(species)) {
    return null;
  }
  if (typeof species === "number" && Number.isFinite(species)) {
    return species;
  }
  const raw = String(species).trim();
  if (/^\d+$/.test(raw)) {
    return parseInt(raw, 10);
  }
  const requestedKey = normalizeSpeciesKey(raw);
  const normalized = normalizeSpeciesList(speciesList);
  const idx = normalized.findIndex(
    (sp) => normalizeSpeciesKey(sp) === requestedKey
  );
  return idx >= 0 ? idx : null;
};

const isMetaMaskProviderLocked = async (provider) => {
  if (!provider?.isMetaMask) {
    return false;
  }
  try {
    const accounts = await provider.request({ method: "eth_accounts" });
    return !Array.isArray(accounts) || accounts.length === 0;
  } catch {
    return true;
  }
};

const normalizeAccountList = (accounts) => {
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return { error: NO_ACCOUNTS_MESSAGE };
  }
  try {
    return {
      value: accounts.map((account) => ethers.utils.getAddress(account)),
    };
  } catch {
    return { error: INVALID_ACCOUNT_MESSAGE };
  }
};

const normalizeRecordTokenType = (tokenType) => {
  const value = String(tokenType || "").toLowerCase();
  if (!value) return "";
  if (value.includes("carbon")) return "carbon";
  if (value.includes("agro")) return "agro";
  return value;
};

const filterUnifiedBalances = (
  records,
  { tokenType, projectId, draftId, year, tokenAddress }
) => {
  const balances = records?.erc1155_balances || [];
  return balances.filter((entry) => {
    if (tokenType && normalizeRecordTokenType(entry.token_type) !== tokenType) {
      return false;
    }
    if (projectId) {
      if (hasMeaningfulValue(entry.project_id)) {
        if (Number(entry.project_id) !== Number(projectId)) {
          return false;
        }
      } else if (tokenAddress) {
        if (
          String(entry.token_address || "").toLowerCase() !==
          String(tokenAddress).toLowerCase()
        ) {
          return false;
        }
      } else {
        return false;
      }
    } else if (tokenAddress) {
      if (
        String(entry.token_address || "").toLowerCase() !==
        String(tokenAddress).toLowerCase()
      ) {
        return false;
      }
    }
    if (draftId) {
      if (hasMeaningfulValue(entry.project_id)) {
        if (Number(entry.project_id) !== Number(draftId)) {
          return false;
        }
      } else if (tokenAddress) {
        if (
          String(entry.token_address || "").toLowerCase() !==
          String(tokenAddress).toLowerCase()
        ) {
          return false;
        }
      } else {
        return false;
      }
    }
    return !(year !== undefined && Number(entry.year) !== Number(year));

  });
};

const resolveOfferingAddress = async ({
  contractAddress: _contractAddress,
  provider: _provider,
  offeringAddress,
  project,
}) => {
  if (offeringAddress) return offeringAddress;
  if (project?.public_offering?.smart_contract_address) {
    return project.public_offering.smart_contract_address;
  }
  if (project?.offering?.smart_contract_address) {
    return project.offering.smart_contract_address;
  }
  return null;
};

if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}

const actions = {
    ...backendActions,
    async ensureNetwork({ commit, dispatch }, requiredChainId) {
      const network = Object.values(networkConfig).find(
        (candidate) =>
          String(candidate.chainId) === String(requiredChainId)
      );
      if (!network) {
        commit(
          "setWalletErrorMessage",
          `Required network ${requiredChainId} is not configured.`
        );
        return false;
      }
      try {
        const provider = await getPreferredProvider();
        let networkId;
        try {
          networkId = await readInjectedWalletChainId(provider);
        } catch {
          const switchResult = await dispatch("switchNetwork", {
            network,
            provider,
          });
          if (!switchResult) return false;
          networkId = await readInjectedWalletChainId(provider);
        }
        const isValidNetwork =
          String(networkId) === String(requiredChainId);
        let resolvedNetworkId = networkId;
        if (!isValidNetwork) {
          const switchResult = await dispatch("switchNetwork", {
            network,
            provider,
          });
          if (!switchResult) return false;
          const newNetworkId = await readInjectedWalletChainId(provider);
          resolvedNetworkId = newNetworkId;
        }
        if (String(resolvedNetworkId) !== String(requiredChainId)) {
          commit(
            "setWalletErrorMessage",
            getNetworkSwitchRequiredMessage(network)
          );
          return false;
        }
        const normalizedNetworkId = String(resolvedNetworkId);
        commit("setNetworkId", normalizedNetworkId);
        const networkSettings = await dispatch(
          "getNetworkSettings",
          normalizedNetworkId
        );
        commit("setNetworkSettings", networkSettings);
        return true;
      } catch {
        commit(
          "setWalletErrorMessage",
          getNetworkSwitchRequiredMessage(network)
        );
        return false;
      }
    },
    async connectMetaMask({ state, commit, dispatch }) {
      try {
        commit("setIsLoading", true);
        const provider = await getPreferredProvider();
        let accounts = await provider.request({ method: "eth_accounts" });
        if (!Array.isArray(accounts) || accounts.length === 0) {
          accounts = await provider.request({ method: "eth_requestAccounts" });
        }
        if (await isMetaMaskProviderLocked(provider)) {
          commit("setWalletErrorMessage", METAMASK_LOCKED_MESSAGE);
          return;
        }
        const { value: normalizedAccounts, error: accountError } =
          normalizeAccountList(accounts);
        if (accountError) {
          commit("setWalletErrorMessage", accountError);
          return;
        }
        const networkReady = await dispatch(
          "ensureNetwork",
          ACTIVE_CHAIN.chainId
        );
        if (!networkReady) return;
        commit("setConnectedStatus", true);
        commit("setAccounts", normalizedAccounts);
        const ethersProvider = createInjectedWeb3Provider(provider);
        commit("setCurrentAccount", {
          address: normalizedAccounts[0],
          type: "MetaMask",
          walletId: state.walletInfo?.walletId || "metamask",
          provider: ethersProvider,
        });
        const adapter = new WalletAdapter(state);
        await adapter.initialize();
        commit("setAdapter", adapter);
        commit("setProvider", adapter.provider || ethersProvider);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        if (extractedMessage === PENDING_REQUEST_MESSAGE) {
          commit(
            "setWalletErrorMessage",
            "Error connecting to MetaMask: " +
              "There is already a pending request at Metamask, please open metamask manually..."
          );
        } else {
          commit("setWalletErrorMessage", extractedMessage);
        }
      } finally {
        commit("setIsLoading", false);
      }
    },
    async connectLedger({ state, commit, dispatch }, derivationPath) {
      let transport;
      try {
        commit("setIsLoading", true);
        transport = await TransportWebUSB.create();
        const eth = new Eth(transport);
        const { address } = await eth.getAddress(derivationPath, {
          display: true,
        });
        await eth.signPersonalMessage(
          derivationPath,
          Buffer.from("Sign in to Brazil Carbon").toString("hex")
        );
        commit("setConnectedStatus", true);
        commit("setAccounts", [address]);
        commit("setCurrentAccount", {
          address: address,
          type: "Ledger",
          derivationPath: derivationPath,
        });
        commit("setNetworkId", String(ACTIVE_CHAIN.chainId));
        const networkSettings = await dispatch(
          "getNetworkSettings",
          String(ACTIVE_CHAIN.chainId)
        );
        commit("setNetworkSettings", networkSettings);
        const adapter = new WalletAdapter(state);
        await adapter.initialize();
        commit("setAdapter", adapter);
        commit("setProvider", adapter.provider);
      } catch (err) {
        let extractedMessage = err.message;
        if (err.message.includes("Locked device (0x5515)")) {
          extractedMessage = "Please unlock your Ledger device.";
        } else if (err.message.includes("UNKNOWN_ERROR (0x6511)")) {
          extractedMessage =
            "Please open the Ethereum app on your Ledger device.";
        }
        commit("setWalletErrorMessage", extractedMessage);
        if (transport) {
          await transport.close();
        }
      } finally {
        commit("setIsLoading", false);
      }
    },
    async connectWeb3Auth({ state, commit, dispatch }, payload) {
      try {
        const signer = payload.ethersProvider.getSigner();
        const address = await signer.getAddress();
        commit("setDialogMetamask", false);
        commit("setWeb3Auth", payload.web3auth);
        commit("setNetworkId", String(ACTIVE_CHAIN.chainId));
        const networkSettings = await dispatch(
          "getNetworkSettings",
          String(ACTIVE_CHAIN.chainId)
        );
        commit("setNetworkSettings", networkSettings);
        commit("setCurrentAccount", {
          address,
          type: "Web3Auth",
          provider: payload.ethersProvider,
        });
        commit("setConnectedStatus", true);
        const adapter = new WalletAdapter(state);
        await adapter.initialize();
        commit("setAdapter", adapter);
        commit("setProvider", adapter.provider);
      } catch (e) {
        console.log(e);
      }
    },
    async connectMetaKeep(
      { state, commit, dispatch },
      { provider, address, email, phone, sdk }
    ) {
      try {
        const network = await provider.getNetwork();
        const networkId = network.chainId.toString();
        const networkSettings = await dispatch("getNetworkSettings", networkId);
        const storedMetaKeep = getStoredMetaKeepSession();
        const businessWallet = storedMetaKeep.businessWalletAddress || null;
        commit("setNetworkSettings", networkSettings);
        commit("setConnectedStatus", true);
        commit("setAccounts", businessWallet ? [businessWallet] : []);
        commit("setCurrentAccount", {
          address: businessWallet,
          businessWalletAddress: businessWallet,
          authAddress: address,
          type: "MetaKeep",
          provider,
          email,
          phone,
          sdk,
        });
        const adapter = new WalletAdapter(state);
        await adapter.initialize();
        commit("setAdapter", adapter);
        commit("setProvider", adapter.provider);
      } catch (e) {
        console.log(e);
      }
    },
    async fetchLedgerAccounts({ commit }) {
      try {
        commit("setIsLoading", true);
        const transport = await TransportWebUSB.create();
        const eth = new Eth(transport);
        const accounts = [];
        for (let i = 0; i < 5; i++) {
          // Fetch 5 accounts
          //let accountPath = `44'/60'/${i}'/0/0`; HARDHAT ETH
          let accountPath = `44'/60'/0'/0/${i}`; // BIP44 PATH
          const { address } = await eth.getAddress(accountPath);
          accounts.push(address);
        }
        return accounts;
      } catch (err) {
        let extractedMessage = err.message;

        if (err.message.includes("Locked device (0x5515)")) {
          extractedMessage = "Please unlock your Ledger device.";
        } else if (err.message.includes("UNKNOWN_ERROR (0x6511)")) {
          extractedMessage =
            "Please open the Avalanche app on your Ledger device.";
        }
        //else if (err.message.includes('Condition of use not satisfied (denied by the user?) (0x6985)')) {
        //  extractedMessage = "Denied by user.";
        //}
        commit("setWalletErrorMessage", extractedMessage);
        if (transport) {
          await transport.close();
        }
        return [];
      } finally {
        commit("setIsLoading", false);
      }
    },
    async disconnectLedger() {
      try {
        const transport = await TransportWebUSB.create();
        await transport.close();
        commit("setAdapter", null);
        commit("setProvider", null);
        await dispatch("callGetProvider");
      } catch (error) {
        //console.error("Error closing Ledger transport:", error);
      }
    },
    async getNetworkId({ commit}) {
      const provider = await getPreferredProvider();
      const networkId = await readInjectedWalletChainId(provider);
      commit("setNetworkId", String(networkId));
    },
    async getNetworkSettings({ commit }, network) {
      const matchingNetwork = Object.values(networkConfig).find(
        (config) => String(config.chainId) === String(network)
      );
      if (!matchingNetwork) return;

      commit("setNetworkName", matchingNetwork.name);

      // Active deployment is the current Carbon V2 stack. Keep its factory
      // separate from the Agro factory field used by legacy/agro actions.
      if (String(network) !== "31337") {
        try {
          const resp = await axios.get(
            getURLFromPieces("admin", "active-deployment", String(network))
          );
          if (resp.data?.factory_address) {
            const deployedAddresses = resp.data.deployed_addresses || {};
            return {
              ...matchingNetwork,
              usdcTokenContract:
                deployedAddresses.CARBON_V2_STABLECOIN || resp.data.stablecoin_address,
              carbonV2StakingDistributorContract:
                deployedAddresses.CARBON_V2_RECEIPT_STAKING_DISTRIBUTOR,
              bctTokenContract: deployedAddresses.CARBON_V2_BCT,
              carbonV2YieldRouterContract:
                deployedAddresses.CARBON_V2_YIELD_ROUTER,
              carbonV2TimelockContract: deployedAddresses.CARBON_V2_TIMELOCK,
              carbonV2TransferPolicyContract: deployedAddresses.CARBON_V2_TRANSFER_POLICY,
              carbonV2TrustRegistryContract: deployedAddresses.CARBON_V2_TRUST_REGISTRY,
              carbonV2VerifiedCreditContract: deployedAddresses.CARBON_V2_VERIFIED_CREDIT,
              carbonV2EvidenceRegistryContract: deployedAddresses.CARBON_V2_EVIDENCE_REGISTRY,
              carbonV2ProjectLogicContract: deployedAddresses.CARBON_V2_PROJECT_LOGIC,
              carbonV2ReceiptLogicContract: deployedAddresses.CARBON_V2_RECEIPT_LOGIC,
              carbonV2SettlementVaultLogicContract:
                deployedAddresses.CARBON_V2_SETTLEMENT_VAULT_LOGIC,
              carbonV2FactoryContract: resp.data.factory_address,
              agroforestryProjectFactoryContract:
                deployedAddresses.AGRO_FACTORY,
              kycWhitelistContract: deployedAddresses.KYC_WHITELIST,
              carbonV2StakingRewardSourceRegistryContract:
                deployedAddresses.CARBON_V2_STAKING_REWARD_SOURCE_REGISTRY,
              carbonV2TreasuryStakingRelayManagerContract:
                deployedAddresses.CARBON_V2_TREASURY_STAKING_RELAY_MANAGER,
              carbonV2MarketplaceContract: deployedAddresses.CARBON_V2_MARKETPLACE,
              carbonV2StablecoinContract: resp.data.stablecoin_address,
              carbonV2FeeConfig:
                resp.data.fee_config || deployedAddresses.CARBON_V2_FEE_CONFIG || null,
            };
          }
        } catch {
          // No active deployment in DB — fall through to static config
        }
      }

      return matchingNetwork;
    },
    async addNetwork({}, { network, provider }) {
      const params = {
        chainId: `0x${parseInt(network.chainId).toString(16)}`,
        chainName: network.name,
        rpcUrls: [network.rpcUrl],
        nativeCurrency: {
          name: network.symbol,
          symbol: network.symbol,
          decimals: 18,
        },
      };
      if (network.blockExplorerUrl) {
        params.blockExplorerUrls = network.blockExplorerUrl || [];
      }
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [params],
      });
      return true;
    },
    async switchNetwork(
      { commit, dispatch },
      { network, failed, provider: providedProvider }
    ) {
      let provider = providedProvider;
      try {
        provider = provider || (await getPreferredProvider());
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${parseInt(network.chainId).toString(16)}` }],
        });
        return true;
      } catch (error) {
        if (
          Number(error?.code) === 4902 &&
          !failed
        ) {
          try {
            await dispatch("addNetwork", { network, provider });
            return await dispatch("switchNetwork", {
              network,
              failed: true,
              provider,
            });
          } catch (e) {
            commit(
              "setWalletErrorMessage",
              getStaleRpcNetworkMessage(e, network) ||
                (Number(e?.code) === 4001
                  ? NETWORK_SWITCH_REJECTED_MESSAGE
                  : getNetworkSwitchRequiredMessage(network))
            );
            return false;
          }
        } else if (Number(error?.code) === 4001) {
          commit("setWalletErrorMessage", NETWORK_SWITCH_REJECTED_MESSAGE);
          return false;
        } else {
          commit(
            "setWalletErrorMessage",
            getNetworkSwitchRequiredMessage(network)
          );
          return false;
        }
      }
    },
    async syncWallet(
      { state, commit, dispatch },
      { walletProvider, walletId }
    ) {
      if (!state.dialogMetamask) return;
      if (
        state.currentAccount &&
        (state.currentAccount.type === "Ledger" ||
          state.currentAccount.type === "Web3Auth")
      ) {
        return;
      }
      if (window.ethereum) {
        const provider = walletProvider;
        // Handle initial account
        const accounts = await provider.request({
          method: "eth_requestAccounts",
        });
        commit("setAccounts", accounts);
        //commit('setCurrentAccount', {address: accounts[0], type: "MetaMask"});
        const networkId = await readInjectedWalletChainId(provider);
        if (String(networkId) !== String(state.networkId)) {
          const networkToSwitch = Object.values(networkConfig).find(
            (net) => parseInt(net.chainId) === parseInt(state.networkId)
          );
          const switchSuccessful = await dispatch("switchNetwork", {
            network: networkToSwitch,
            provider,
          });
          if (!switchSuccessful) {
            commit("setNetworkId", String(networkId));
          }
        }
        const previousListeners = injectedProviderListeners.get(provider);
        const canRemoveListeners =
          typeof provider.removeListener === "function";
        if (previousListeners && canRemoveListeners) {
          provider.removeListener(
            "accountsChanged",
            previousListeners.accountsChanged
          );
          provider.removeListener("chainChanged", previousListeners.chainChanged);
        }

        // Listen for account changes
        const accountsChanged = async (accounts) => {
          if (!state.isConnected) return;
          const nextAccounts = Array.isArray(accounts) ? accounts : [];
          commit("setIsAuthenticated", false);
          commit("setAccounts", nextAccounts);
          commit("setCarbonSession", {
            businessWalletAddress: null,
            authWalletAddress: null,
          });
          commit("setBalances", {});
          commit("setUSDCBalance", 0);
          commit("setBctBalance", 0);
          commit("setAdapter", null);
          commit("setProvider", null);
          const clearedSession = clearAuthenticatedWalletSession();
          if (nextAccounts.length === 0) {
            // User has disconnected their wallet
            commit("setConnectedStatus", false);
            commit("setCurrentAccount", {});
            commit("setNetworkId", -1);
            await clearedSession;
          } else {
            const ethersProvider = createInjectedWeb3Provider(provider);
            commit("setCurrentAccount", {
              address: nextAccounts[0],
              type: "MetaMask",
              provider: ethersProvider,
              walletId,
            });
            await clearedSession;
            const adapter = new WalletAdapter(state);
            await adapter.initialize();
            commit("setAdapter", adapter);
            commit("setProvider", adapter.provider || ethersProvider);
          }
        };
        // Listen for network changes
        const chainChanged = async (chainId) => {
          if (!state.isConnected) return;
          const numericChainId = parseInt(chainId, 16);
          commit("setNetworkId", numericChainId.toString());
          const networkSettings = await dispatch(
            "getNetworkSettings",
            numericChainId.toString()
          );
          commit("setNetworkSettings", networkSettings);
        };
        if (!previousListeners || canRemoveListeners) {
          provider.on("accountsChanged", accountsChanged);
          provider.on("chainChanged", chainChanged);
          injectedProviderListeners.set(provider, {
            accountsChanged,
            chainChanged,
          });
        }
      }
    },
    async connectWallet(
      { state, commit, dispatch },
      { walletProvider, walletId }
    ) {
      try {
        commit("setIsLoading", true);
        commit("setWalletInfo", { walletId, provider: walletProvider });

        let accounts = await walletProvider.request({
          method: "eth_accounts",
        });
        if (!Array.isArray(accounts) || accounts.length === 0) {
          accounts = await walletProvider.request({
            method: "eth_requestAccounts",
          });
        }
        if (await isMetaMaskProviderLocked(walletProvider)) {
          commit("setWalletErrorMessage", METAMASK_LOCKED_MESSAGE);
          return;
        }
        const { value: normalizedAccounts, error: accountError } =
          normalizeAccountList(accounts);
        if (accountError) {
          commit("setWalletErrorMessage", accountError);
          return;
        }
        const networkReady = await dispatch(
          "ensureNetwork",
          ACTIVE_CHAIN.chainId
        );
        if (!networkReady) return;
        commit("setConnectedStatus", true);
        commit("setAccounts", normalizedAccounts);
        const p = createInjectedWeb3Provider(walletProvider);
        commit("setCurrentAccount", {
          address: normalizedAccounts[0],
          type: "MetaMask",
          walletId,
          provider: p,
        });

        const adapter = new WalletAdapter(state);
        await adapter.initialize();
        commit("setAdapter", adapter);
        commit("setProvider", adapter.provider);
      } catch (err) {
        const extracted = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extracted);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callMintFunction({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        console.log("[callMintFunction] Creating offering...");
        const resolvedAddress =
            payload.contractAddress ||
            payload.projectAddress ||
            payload.smartContractAddress ||
            state.createdProject ||
            "";
        if (!resolvedAddress) {
            commit(
              "setWalletErrorMessage",
              "Unified contracts require a project address to continue."
            );
            return;
        }
        commit("setCreatedOffering", resolvedAddress);
        return { offering_address: resolvedAddress };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    /**
     * Deploy a public credit sale contract via the factory.
     * Requires the caller to be a factory owner (onlyOwner).
     *
     * @param {Object} payload
     * @param {string} payload.biotokenAddress  - ERC-1155 investment token address
     * @param {string} payload.partnerAddress   - Partner fee recipient
     * @param {string} payload.operatorAddress  - Operator/platform fee recipient
     * @param {number} payload.usdCommission    - Platform commission %
     * @param {number} payload.partnerUsdCommission - Partner commission %
     * @returns {{ saleAddress: string }} Address of the deployed sale contract
     */
    async deployPublicCreditSale({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const networkSettings = await dispatch(
          "getNetworkSettings",
          state.networkId
        );
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const factoryAddress =
          networkSettings.agroforestryProjectFactoryContract;
        const { buildPublicSaleArgs } = await import(
          "src/utils/contractConfig"
        );
        const args = buildPublicSaleArgs({
          biotokenAddress: payload.biotokenAddress,
          projectAddress: payload.projectAddress,
          partnerAddress: payload.partnerAddress,
          operatorAddress: payload.operatorAddress,
          usdCommission: payload.usdCommission,
          partnerUsdCommission: payload.partnerUsdCommission,
        });
        const methodName = payload.startImmediately
          ? "createStartedPublicCreditSale"
          : "createPublicCreditSale";
        const methodArgs = payload.startImmediately
          ? [args[0], args[2], args[3], args[4], args[5]]
          : args;
        const { receipt } = await adapter.executeMethod(
          factoryAddress,
          BRAZIL_FACTORY_ABI,
          methodName,
          methodArgs
        );
        const factoryIface = new ethers.utils.Interface(BRAZIL_FACTORY_ABI);
        const saleEvent = (receipt?.logs || [])
          .map((log) => {
            try {
              return factoryIface.parseLog(log);
            } catch {
              return null;
            }
          })
          .find((e) => e?.name === "PublicCreditSaleCreated");
        const saleAddress = saleEvent?.args?.sale;
        console.log(
          "[deployPublicCreditSale] event:",
          saleEvent
            ? "PublicCreditSaleCreated found"
            : "PublicCreditSaleCreated NOT found"
        );
        if (!saleAddress) {
          throw new Error(
            "PublicCreditSaleCreated event not found in transaction receipt."
          );
        }
        return { saleAddress };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getNativeCreditTokenAddress({ state, dispatch }) {
      const networkSettings = await dispatch(
        "getNetworkSettings",
        state.networkId
      );
      const provider = await dispatch("callGetProvider");
      const factory = new ethers.Contract(
        networkSettings.agroforestryProjectFactoryContract,
        BRAZIL_FACTORY_ABI,
        provider
      );
      return factory.nativeCreditToken();
    },
    async createListedCreditSeries({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const abi = ABI_REGISTRY.BrazilCarbonUniversalCredit1155.abi;
        const { receipt } = await adapter.executeMethod(
          payload.creditTokenAddress,
          abi,
          "createListedSeries",
          [payload.seriesInput]
        );
        const iface = new ethers.utils.Interface(abi);
        const seriesEvent = (receipt?.logs || [])
          .map((log) => {
            try {
              return iface.parseLog(log);
            } catch {
              return null;
            }
          })
          .find((event) => event?.name === "SeriesCreated");
        const tokenId = seriesEvent?.args?.tokenId;
        if (tokenId === undefined) {
          throw new Error("SeriesCreated event not found in transaction receipt.");
        }
        return { tokenId: tokenId.toString() };
      } catch (err) {
        commit("setWalletErrorMessage", extractMetamaskErrorMessage(err));
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async listDirectCreditForSale({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const accountAddress = state.currentAccount?.address;
        const response = await listCreditTokenForSaleWithYear({
          adapter,
          accountAddress,
          creditTokenAddress: payload.creditTokenAddress,
          saleAddress: payload.saleAddress,
          tokenId: payload.tokenId,
          amountUnits: payload.amountUnits,
          listingPriceUnits: payload.listingPriceUnits,
          year: payload.year,
        });
        return { res: true, response };
      } catch (err) {
        commit("setWalletErrorMessage", extractMetamaskErrorMessage(err));
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async mintPublicOffering({ state, dispatch, commit }, payload) {
      try {
        const contractAddress = payload.projectAddress;
        const brazilCarbonProjectAbi = ABI_REGISTRY.BrazilCarbonCreditUnified;
        const contractABI = brazilCarbonProjectAbi.abi;
        const methodName = "seedPublicOffering";
        const mintParams = {
          amountToMint: ethers.utils.parseUnits(payload.amount.toString(), 2),
          year: payload.year,
          user: payload.user,
          price: ethers.utils.parseUnits(payload.price.toString(), 2),
          operatorFee: payload.platforFees,
          developerFee: payload.developerFees,
        };
        const adapter = toRaw(state.adapter);
        const accountAddress = state.currentAccount?.address;
        if (!accountAddress) {
          throw new Error("Wallet is not connected.");
        }
        const projectContract = new ethers.Contract(
          contractAddress,
          contractABI,
          adapter.signer
        );
        const investmentTokenAddress = await projectContract.investmentToken();
        const deployResult = await dispatch("deployPublicCreditSale", {
          biotokenAddress: investmentTokenAddress,
          projectAddress: contractAddress,
          partnerAddress: accountAddress,
          operatorAddress: accountAddress,
          usdCommission: mintParams.operatorFee,
          partnerUsdCommission: mintParams.developerFee,
        });
        const publicOfferingAddress = deployResult?.saleAddress;
        if (!publicOfferingAddress) {
          throw new Error("Public sale deployment did not return an address.");
        }
        const usdcContractAddress = state.networkSettings.usdcTokenContract;
        const usdcContract = new ethers.Contract(
          usdcContractAddress,
          USDC_ABI,
          adapter.signer
        );
        const allowance = await usdcContract.allowance(
          accountAddress,
          contractAddress
        );
        if (!allowance.eq(ethers.constants.MaxUint256)) {
          await adapter.executeMethod(
            usdcContractAddress,
            USDC_ABI,
            "approve",
            [contractAddress, ethers.constants.MaxUint256]
          );
        }
        await adapter.executeMethod(
          contractAddress,
          contractABI,
          methodName,
          [
            publicOfferingAddress,
            mintParams.amountToMint,
            mintParams.year,
            mintParams.user,
            mintParams.price,
          ]
        );

        return publicOfferingAddress;
      } catch (e) {
        console.log(e);
      }
    },
    async callGetOfferingState({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      let networkId = payload.networkId;
      if (!(networkId in CHAIN_CONFIG)) {
        const tmpProvider = await getPreferredProvider();
        networkId = await readInjectedWalletChainId(tmpProvider);
        commit("setNetworkId", String(networkId));
      }
      const provider = await dispatch("callGetProvider");
      try {
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const offeringState = await offeringContract.offeringState();
        commit("setOfferingState", offeringState);
      } catch (err) {
        const code = await provider.getCode(payload.contractAddress);
        if (code === "0x" && process.env.VUE_APP_ENV === TESTING_ENV) {
          commit("setOfferingState", OFFERING_STATES.NO_OFFERING);
        }
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async addAdminToFactory({ state }, payload) {
      const adapter = toRaw(state.adapter);
      const abi = ABI_REGISTRY.AgroforestryProjectFactory;
      const methodName = "addOwner";
      const contractAddress =
        state.networkSettings.agroforestryProjectFactoryContract;
      const contractFactory = new ethers.Contract(
        contractAddress,
        abi.abi,
        adapter.signer
      );
      const isAdmin = await contractFactory.inOwners(payload.newAdmin);
      if (!isAdmin) {
        await adapter.executeMethod(contractAddress, abi.abi, methodName, [
          payload.newAdmin,
        ]);
      }
    },
    async removeAdmin({ state }, payload) {
      const adapter = toRaw(state.adapter);
      const abi = ABI_REGISTRY.AgroforestryProjectFactory;
      const methodName = "removeOwner";
      const contractAddress =
        state.networkSettings.agroforestryProjectFactoryContract;
      const contractFactory = new ethers.Contract(
        contractAddress,
        abi.abi,
        adapter.signer
      );
      const isAdmin = await contractFactory.inOwners(payload.adminToRemove);
      if (isAdmin) {
        await adapter.executeMethod(contractAddress, abi.abi, methodName, [
          payload.adminToRemove,
        ]);
      }
    },
    async callStartFunction({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "start";
        await adapter.executeMethod(payload.contractAddress, abi, methodName);
        return true;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    // NOTE: This is a read-only balance aggregator, not a contract write.
    // All on-chain reads (resolve credit address/tokenId, ERC-1155 balanceOf)
    // and balances dict assembly should move to the caller (ConnectButton.vue).
    // TODO: Move this logic to ConnectButton.vue (or a composable) and
    // delete this action. Caller should commit("setBalances", ...) directly.
    async callUpdateCreditBalance({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        const signerAddress = await signer.getAddress();
        const balancesDict = state.balances ?? {};
        const allocatedOfferingsDict = state.allocatedOfferings ?? {};
        Promise.allSettled(
          payload.contractAddresses.map(async (item) => {
            let creditAddress = item.carbon_credit_address;
            let tokenId =
              item.carbon_credit_token_id ??
              item.carbon_credit_tokenId ??
              item.token_id;
            if (item.project_address) {
              const projectAbi = BRAZIL_CREDIT_ABI;
              const projectContract = new ethers.Contract(
                item.project_address,
                projectAbi,
                provider
              );
              try {
                const [resolvedAddress, resolvedTokenId] =
                  await Promise.all([
                    projectContract.carbonCreditToken(),
                    projectContract.carbonCreditTokenId(),
                  ]);
                creditAddress = resolvedAddress;
                tokenId = resolvedTokenId;
              } catch {}
            }
            if (
              !creditAddress ||
              creditAddress === ethers.constants.AddressZero ||
              tokenId === null ||
              tokenId === undefined
            ) {
              return;
            }
            const credit1155 = new ethers.Contract(
              creditAddress,
              ERC1155_ABI,
              provider
            );
            const balanceBig = await credit1155.balanceOf(
              signerAddress,
              tokenId
            );
            const balance = ethers.utils.formatUnits(
              balanceBig.toString(),
              TOKEN_DECIMALS
            );
            if (Number(balance) > 0) {
              const key = item.project_address || creditAddress;
              balancesDict[key] = balance;
              allocatedOfferingsDict[key] = item.price;
            }
          })
        ).then(() => {
          commit("setBalances", balancesDict);
        });
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callGetRemainingAllocationFunction(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        if (state.networkId !== payload.networkId) {
          commit(
            "setWalletErrorMessage",
            `Wrong network Id need to change : ${payload.networkId}`
          );
          const requiredNetworkName = CHAIN_CONFIG[payload.networkId]?.name;
          commit("setRequiredNetworkName", requiredNetworkName);
        }
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          signer
        );
        const remainingAllocation = ethers.utils.formatUnits(
          (
            await offeringContract.getRemainingAllocation(payload.funderAddress)
          ).toString(),
          TOKEN_DECIMALS
        );
        commit("setRemainingAllocation", remainingAllocation);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callGetUSDCBalanceFunction({ state, commit, dispatch }) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        if (!state.networkSettings) {
          //console.error("Network settings are not set");
          return;
        }
        const usdcContract = new ethers.Contract(
          state.networkSettings.usdcTokenContract,
          USDC_ABI,
          provider
        ).connect(signer);
        const balance = await usdcContract.balanceOf(
          state.currentAccount.address
        );
        const usdcBalance = ethers.utils.formatUnits(balance, DECIMAL_UNITS);
        commit("setUSDCBalance", usdcBalance);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callFetchGasPriceFunction({commit, dispatch }) {
      try {
        const provider = await dispatch("callGetProvider");
        const gasLimitValue = ethers.utils.parseUnits("1000000", "wei");
        const price = await provider.getGasPrice();
        const gasCost = price.mul(gasLimitValue);
        commit("setGasPrice", ethers.utils.formatEther(gasCost));
      } catch (error) {
        //console.error('Error fetching gas price:', error);
      }
    },
    // Pure contract bridge: USDC approve + bid/invest.
    // Caller must provide { contractAddress, amount, approvalAmount (BigNumber), abi? }.
    // Removed: balance check, allowance check, totalDollarSpend parsing, txURL
    // build, post-bid getRemainingAllocation query, USDC balance refresh.
    async callBidFunction({ state, commit }, payload) {
      try {
        console.log("[callBidFunction] Executing bid...", "amount:", payload.amount);
        commit("setBidResult", null);
        commit("setApprovalResult", null);
        const adapter = toRaw(state.adapter);
        const abi = payload.abi || CARBON_OFFERING_ABI;

        // Step 1: USDC approve
        try {
          await adapter.approve(
            state.networkSettings.usdcTokenContract,
            payload.contractAddress,
            payload.approvalAmount
          );
          commit("setApprovalResult", true);
        } catch (err) {
          commit("setApprovalResult", false);
          throw err;
        }

        // Step 2: execute bid/invest
        const gasLimitValue = ethers.utils.parseUnits("1000000", "wei");
        const { response } = await adapter.executeMethod(
          payload.contractAddress,
          abi,
          "bid",
          [payload.amount],
          { gasLimit: gasLimitValue }
        );

        commit("setBidResult", true);
        commit("setWalletErrorMessage", "");
        return response;
      } catch (err) {
        commit("setApprovalResult", false);
        commit("setBidResult", false);
        if (
          err.code === "ACTION_REJECTED" ||
          err.message?.includes("user rejected")
        ) {
          commit(
            "setWalletErrorMessage",
            "Transaction was rejected by the user."
          );
        } else {
          const extractedMessage = extractMetamaskErrorMessage(err);
          commit("setWalletErrorMessage", extractedMessage);
        }
        return null;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getRemainingCommissionAmount({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          signer
        );
        const remainingUsdCommission = ethers.utils.formatUnits(
          await offeringContract.usdCommissionAmount(),
          DECIMAL_UNITS
        );
        const remainingUsdCommissionNum = Number.parseFloat(
          remainingUsdCommission
        );
        commit("setUsdCommission", remainingUsdCommissionNum);

        const usdCommissionWithdrawn =
          await offeringContract.usdCommissionWithdrawn();
        commit("setUsdCommissionTransferred", usdCommissionWithdrawn);

        return remainingUsdCommissionNum;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getRemainingCarbonCreditCommissionAmount(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          signer
        );
        const remainingCreditCommission = ethers.utils.formatUnits(
          await offeringContract.usdCommissionAmount(),
          DECIMAL_UNITS
        );
        const remainingCreditCommissionNum = Number.parseFloat(
          remainingCreditCommission
        );
        commit("setCarbonCreditCommission", remainingCreditCommissionNum);

        const usdCommissionWithdrawn =
          await offeringContract.usdCommissionWithdrawn();
        commit(
          "setCarbonCreditCommissionsTransferred",
          usdCommissionWithdrawn
        );

        return remainingCreditCommissionNum;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getRemainingDeveloperCommissionAmount(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          signer
        );
        const developerCommissionTransfered =
          await offeringContract.partnerUsdCommissionWithdrawn();
        commit("setDeveloperCommissionTransfered", developerCommissionTransfered);
        const remainingDeveloperCommission = ethers.utils.formatUnits(
          await offeringContract.partnerUsdCommissionAmount(),
          DECIMAL_UNITS
        );
        commit("setRemainingDeveloperCommission", remainingDeveloperCommission);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getRemainingDeveloperCarbonCreditCommissionAmount(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const signer = provider.getSigner(state.currentAccount.address);
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          signer
        );
        const developerUsdCommissionWithdrawn =
          await offeringContract.partnerUsdCommissionWithdrawn();
        commit(
          "setDeveloperCarbonCreditCommissionsTransferred",
          developerUsdCommissionWithdrawn
        );
        const remainingDeveloperCarbonCreditCommission = ethers.utils.formatUnits(
          await offeringContract.partnerUsdCommissionAmount(),
          TOKEN_DECIMALS
        );
        commit(
          "setRemainingDeveloperCarbonCreditCommission",
          remainingDeveloperCarbonCreditCommission
        );
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callWithdrawCommissionFunction({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        commit("setWithdrawCommission", null);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "withdrawCommission";
        await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName
        );
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          abi,
          adapter.signer
        );
        const remainingUsdCommission = ethers.utils.formatUnits(
          await offeringContract.usdCommissionAmount(),
          DECIMAL_UNITS
        );
        const remainingUsdCommissionNum = parseFloat(remainingUsdCommission);
        const usdCommissionWithdrawn =
          await offeringContract.usdCommissionWithdrawn();

        commit("setWithdrawCommission", true);
        commit("setCommissionWithdrawn", remainingUsdCommissionNum);
        commit("setUsdCommission", remainingUsdCommissionNum);
        await dispatch("callGetCurrentUserUSDCBalance");
        return {
          status: true,
          result: usdCommissionWithdrawn,
        };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        commit("setWithdrawCommission", false);
        return { status: false };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callWithdrawCarbonCreditCommissionFunction(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setWithdrawCommission", null);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "withdrawCommission";
        await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName
        );
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          abi,
          adapter.signer
        );
        const remainingCreditCommission = ethers.utils.formatUnits(
          await offeringContract.usdCommissionAmount(),
          DECIMAL_UNITS
        );
        const remainingCreditCommissionNum = parseFloat(
          remainingCreditCommission
        );
        const usdCommissionWithdrawn =
          await offeringContract.usdCommissionWithdrawn();
        commit("setWithdrawCommission", true);
        commit("setCommissionWithdrawn", remainingCreditCommissionNum);
        commit("setCarbonCreditCommission", remainingCreditCommissionNum);
        return {
          status: true,
          result: usdCommissionWithdrawn,
        };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        commit("setWithdrawCommission", false);
        return { status: false };
      }
    },
    async getRemainingFundsUnlockedFunction(
      { state, commit, dispatch },
      payload
    ) {
      try {
        commit("setIsLoading", true);
        commit(
          "setWalletErrorMessage",
          "Unlocked funds are not available in unified contracts."
        );
        const remainingFundsUnlocked = "0";
        commit("insertIntoRemainingFundsUnlocked", {
          key: payload.contractAddress,
          value: remainingFundsUnlocked,
        });
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getAuditFunction({ state, commit, dispatch }, _payload) {
      try {
        commit("setIsLoading", true);
        commit(
          "setWalletErrorMessage",
          "Audit window status is not available in unified contracts."
        );
        commit("setAuditWindowOpen", null);
        commit("setSuccessfulAudit", null);
        commit("setUnsuccessfulAudit", null);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getUnsuccessfulAudit({ state, commit, dispatch }, _payload) {
      try {
        commit("setIsLoading", true);
        commit(
          "setWalletErrorMessage",
          "Audit status is not available in unified contracts."
        );
        commit("setUnsuccessfulAudit", null);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getAbilityToWithdraw({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const { contractAddress } = payload;
        const adapter = toRaw(state.adapter);
        const offeringContract = new ethers.Contract(
          contractAddress,
          CARBON_OFFERING_ABI,
          adapter.signer
        );
        const isCancelled = await offeringContract.cancelled();
        commit("setIsCancelled", isCancelled);
        if (isCancelled) {
          const userAddress = state.currentAccount.address;
          const bidderInfo = await offeringContract.getBidderInfo(userAddress);
          commit("setFundsPaid", bidderInfo.fundsPaid);
          commit("setPotentialRefund", bidderInfo.fundsPaid);
          commit("setIsEligibleForWithdrawingBackFunds", true);
          commit("setCouldWithdrawingBackFunds", false);
        } else {
          commit("setFundsPaid", 0);
          commit("setPotentialRefund", 0);
          commit("setIsEligibleForWithdrawingBackFunds", false);
          commit("setCouldWithdrawingBackFunds", false);
        }
      } catch (error) {
        console.error("Error fetching withdrawal ability:", error);
        commit("setIsCancelled", false);
        commit("setIsEligibleForWithdrawingBackFunds", false);
        commit("setCouldWithdrawingBackFunds", false);
        commit("setFundsPaid", 0);
        commit("setPotentialRefund", 0);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async sendTokensForUnsuccessfulAudit({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callGetAuditOpenAgro({ state: _state, dispatch: _dispatch }, _payload) {
      return true;
    },
    async startAuditAgro({ state: _state }, _payload) {
      return true;
    },
    async callStartAuditFunction({ state, commit }, _payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        await adapter.executeMethod(
          _payload.contractAddress,
          BRAZIL_CREDIT_ABI,
          "startAudit",
          []
        );
        commit("setAuditWindowOpen", true);
        return { status: true, result: true };
      } catch (err) {
        console.log(err);
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return { status: false, result: false };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callPauseAuditFunction({ state, commit }, _payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        await adapter.executeMethod(
          _payload.contractAddress,
          BRAZIL_CREDIT_ABI,
          "pauseForUnsuccessfulAudit",
          []
        );
        commit("setUnsuccessfulAudit", true);
        return { status: true, result: true };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return { status: false, result: false };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async cancelUnsuccessfulAudit({ state, commit }, _payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        await adapter.executeMethod(
          _payload.contractAddress,
          BRAZIL_CREDIT_ABI,
          "cancelUnsuccessfulAudit",
          []
        );
        commit("setUnsuccessfulAudit", false);
        return { status: true, result: true };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return { status: false, result: false };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callConfirmAuditFunction({ state, commit }, _payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        await adapter.executeMethod(
          _payload.contractAddress,
          BRAZIL_CREDIT_ABI,
          "confirmAuditSuccess",
          []
        );
        commit("setSuccessfulAudit", true);
        return { status: true, result: true };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return { status: false, result: false };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getAuditSuccessNumber({ state, commit, dispatch }, _payload) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const contract = new ethers.Contract(
          _payload.contractAddress,
          BRAZIL_CREDIT_ABI,
          provider
        );
        const result = await contract.successfulAuditNumber();
        const value = result?.toNumber ? result.toNumber() : Number(result);
        commit("setSuccessfulAudit", value);
        return value;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return null;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getListingEndFunction({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        let provider;
        if (state.currentAccount.type === "Ledger") {
          provider = new ethers.providers.getDefaultProvider(
            CHAIN_CONFIG[state.networkId].rpcEndPoint
          );
        } else {
          provider = await getPreferredProviderEthers(
            state.currentAccount?.provider,
            state.currentAccount?.walletId
          );
        }
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const listingEndBigNumber = await offeringContract.listingEnd();
        // Convert BigNumber to a number (in milliseconds)
        const listingEndTimestamp = listingEndBigNumber.toNumber() * 1000;
        const paymentSlices = await offeringContract.paymentSlice();
        commit("setListingEnd", listingEndTimestamp);
        commit("setPaymentSlices", paymentSlices);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getListingEnd({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        let provider;
        if (state.currentAccount.type === "Ledger") {
          provider = new ethers.providers.getDefaultProvider(
            CHAIN_CONFIG[state.networkId].rpcEndPoint
          );
        } else {
          provider = await getPreferredProviderEthers(
            state.currentAccount?.provider,
            state.currentAccount?.walletId
          );
        }
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const listingEndBigNumber = await offeringContract.listingEnd();
        // Convert BigNumber to a number (in milliseconds)
        const listingEndTimestamp = listingEndBigNumber.toNumber() * 1000;
        commit("setListingEnd", listingEndTimestamp);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callNextUnlockTimeFunction({ state, commit, dispatch }, _payload) {
      try {
        commit("setIsLoading", true);
        commit(
          "setWalletErrorMessage",
          "Unlock times are not available in unified contracts."
        );
        const nextUnlockTime = null;
        commit(
          "setNextUnlockTime",
          nextUnlockTime ? new Date(nextUnlockTime * 1000).toLocaleString() : null
        );
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    // Pure contract bridge: just executes withdrawFunds.
    // Removed: txURL build, post-withdraw getFundsUnlocked query,
    // insertIntoRemainingFundsUnlocked commit, USDC balance refresh.
    // Caller should handle post-withdraw reads if needed.
    async callWithdrawFunction({ state, commit }, payload) {
      try {
        console.log("[callWithdrawFunction] Withdrawing funds...");
        commit("setWithdrawResult", null);
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        const abi = payload.abi || CARBON_OFFERING_ABI;
        const methodName = "withdrawFunds";
        const { response } = await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName
        );
        commit("setWithdrawResult", true);
        return response;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        commit("setWithdrawResult", false);
        return null;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callEndOffering({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        commit("setOfferingState", null);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "end";
        await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName
        );
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          abi,
          adapter.signer
        );
        const offeringState = await offeringContract.offeringState();
        commit("setOfferingState", offeringState);
        return true;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callIsEnoughtToMintCarbonProject() {
      return { res: true };
    },
    async fetchFundsCollected({ commit, state, dispatch }, contractAddress) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const offeringContract = new ethers.Contract(
          contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const fundsCollected = ethers.utils.formatUnits(
          await offeringContract.fundsCollected(),
          DECIMAL_UNITS
        );
        commit("setFundsCollected", fundsCollected);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async validateEthAddress({}, address) {
      try {
        //console.log("Valid Ethereum address:", validAddress);
        return ethers.utils.getAddress(address);
      } catch (error) {
        //console.error("Invalid Ethereum address:", address);
        return false;
      }
    },
    async getWithdrawStatus({ state, commit, dispatch }, payload) {
      try {
        const provider = await dispatch("callGetProvider");
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        commit("setSuccessfulAudit", null);
        const currentPaymentSlice = await offeringContract.paymentSlice();
        commit("setCurrentPaymentSlice", currentPaymentSlice);
      } catch (e) {
        console.log(e);
      }
    },
    async getCurrentPaymentSlice({ state, commit, dispatch }, payload) {
      try {
        const provider = await dispatch("callGetProvider");
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const currentPaymentSlice = await offeringContract.paymentSlice();
        commit("setCurrentPaymentSlice", currentPaymentSlice);
      } catch (e) {
        console.log(e);
      }
    },
    async getPaymentSlices({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        let provider;
        if (state.currentAccount.type === "Ledger") {
          provider = new ethers.providers.getDefaultProvider(
            CHAIN_CONFIG[state.networkId].rpcEndPoint
          );
        } else {
          provider = await getPreferredProviderEthers(
            state.currentAccount?.provider,
            state.currentAccount?.walletId
          );
        }
        const offeringContract = new ethers.Contract(
          payload.contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        const paymentSlices = await offeringContract.paymentSlice();
        commit("setPaymentSlices", paymentSlices);
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async isPausedOffer({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        const { contractAddress } = payload;
        const provider = await dispatch("callGetProvider");
        const offeringContract = new ethers.Contract(
          contractAddress,
          CARBON_OFFERING_ABI,
          provider
        );
        return await offeringContract.paused();
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async isPausedPublicCreditSale({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        const provider = await dispatch("callGetProvider");
        const sale = new ethers.Contract(
          payload.saleAddress,
          ABI_REGISTRY.CarbonCreditPublicSale.abi,
          provider
        );
        return Number(await sale.stage()) === 3;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callPausePublicCreditSale({ state, commit, dispatch }, payload) {
      try {
        commit("setIsLoading", true);
        const adapter = await ensureWalletAdapter({ state, dispatch });
        await adapter.executeMethod(
          payload.saleAddress,
          ABI_REGISTRY.CarbonCreditPublicSale.abi,
          "pause",
          []
        );
        return true;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async pauseOffer({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "pause";
        await adapter.executeMethod(payload.contractAddress, abi, methodName);
        return true;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async unpauseOffer({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "unpause";
        await adapter.executeMethod(payload.contractAddress, abi, methodName);
        return true;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callGetCurrentUserUSDCBalance(
      { state, commit, dispatch },
      { blockTag = null, commitResult = true } = {}
    ) {
      try {
        if (!state.currentAccount?.address) {
          return;
        }
        const refreshedNetworkSettings = await dispatch(
          "getNetworkSettings",
          String(state.networkId)
        );
        if (refreshedNetworkSettings) {
          commit("setNetworkSettings", refreshedNetworkSettings);
        }
        const networkSettings =
          refreshedNetworkSettings || state.networkSettings;
        if (!networkSettings?.usdcTokenContract) {
          return;
        }
        const provider = await dispatch("callGetProvider");
        const usdcContract = new ethers.Contract(
          networkSettings.usdcTokenContract,
          USDC_ABI,
          provider
        );
        const balance =
          blockTag === null || blockTag === undefined
            ? await usdcContract.balanceOf(state.currentAccount.address)
            : await usdcContract.balanceOf(state.currentAccount.address, {
                blockTag,
              });
        const usdcBalance = ethers.utils.formatUnits(balance, DECIMAL_UNITS);
        if (commitResult) {
          commit("setUSDCBalance", usdcBalance);
        }
        return usdcBalance;
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      }
    },
    async getFactoryOwner({ state, commit, dispatch }) {
      try {
        const provider = await dispatch("callGetProvider");
        const contractAddress =
          process.env.AGROFORESTRY_PROJECT_FACTORY_CONTRACT;
        const contractABI = ABI_REGISTRY.AgroforestryProjectFactory;
        const contract = new ethers.Contract(
          contractAddress,
          contractABI.abi,
          provider.getSigner(state.currentAccount.address)
        );
        return await contract.owner();
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
      }
    },
    async getIsApprovedByUser({ state, commit, dispatch }, _payload) {
      try {
        commit(
          "setWalletErrorMessage",
          "Approvals are not supported in unified contracts."
        );
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async callChangeAuditor({ state, commit }, _payload) {
      commit("setIsLoading", true);
      try {
        commit(
          "setWalletErrorMessage",
          "Auditor changes are not supported in unified contracts."
        );
        return false;
      } catch (e) {
        console.log(e);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getRemainingCredits({ state, dispatch }, payload) {
      try {
        const provider = await dispatch("callGetProvider");
        const contractABI = CARBON_OFFERING_ABI;
        const contract = new ethers.Contract(
          payload.contractAddress,
          contractABI,
          provider.getSigner(state.currentAccount.address)
        );
        return await contract.remaining();
      } catch (e) {
        console.log(e);
        return "0";
      }
    },
    async callWithdrawUnsoldTokens({ state, commit }, payload) {
      commit("setIsLoading", true);
      try {
        const adapter = toRaw(state.adapter);
        const abi = CARBON_OFFERING_ABI;
        const methodName = "withdrawUnsoldTokens";
        await adapter.executeMethod(payload.contractAddress, abi, methodName);
        return true;
      } catch (e) {
        console.log(e);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callClearApprovals({ state, commit }, _payload) {
      commit("setIsLoading", true);
      try {
        commit(
          "setWalletErrorMessage",
          "Clearing approvals is not supported in unified contracts."
        );
        return false;
      } catch (e) {
        console.log(e);
        return false;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callGetGroup({ state, commit }, _payload) {
      commit("setIsLoading", true);
      try {
        commit(
          "setWalletErrorMessage",
          "Group approvals are not supported in unified contracts."
        );
        return [];
      } catch (e) {
        console.log(e);
        return [];
      } finally {
        commit("setIsLoading", false);
      }
    },
    // async callMintInvestContract({ state, commit }, payload) {
    //   try {
    //     commit("setIsLoading", true);
    //     const adapter = new WalletAdapter(state);
    //     await adapter.initialize();
    //     const abi = payload.contractABI;
    //     const methodName = "mint";
    //     const params = [
    //       payload.developerAddress,
    //       payload.auditorAddress,
    //       payload.projectTokenParameters,
    //       payload.projectCommissionParameters,
    //       payload.usdcToken,
    //     ];
    //     const response = await adapter.executeMethod(
    //       payload.contractAddress,
    //       abi,
    //       methodName,
    //       params
    //     );
    //     return response;
    //   } catch (error) {
    //     console.error("Error minting investment contract:", error);
    //     throw error;
    //   } finally {
    //     commit("setIsLoading", false);
    //   }
    // },
    async callInvest({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callFarmerDepositAgro(
      { state, commit, dispatch },
      { contractAddress, amount, year, species, speciesList }
    ) {
      try {
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const abi = BRAZIL_AGRO_ABI;
        const parsedAmount = ethers.utils.parseUnits(amount.toString(), 6);
        if (!state.networkSettings) {
          const networkSettings = await dispatch(
            "getNetworkSettings",
            ACTIVE_CHAIN.chainId
          );
          commit("setNetworkSettings", networkSettings);
        }

        const usdc = new ethers.Contract(
          state.networkSettings.usdcTokenContract,
          USDC_ABI,
          adapter.signer
        );
        const bal = await usdc.balanceOf(state.currentAccount.address);
        if (bal.lt(parsedAmount))
          return { res: false, message: "NotEnoughUSDC" };

        const allowance = await usdc.allowance(
          state.currentAccount.address,
          contractAddress
        );
        if (allowance.lt(parsedAmount)) {
          await adapter.executeMethod(
            state.networkSettings.usdcTokenContract,
            USDC_ABI,
            "approve",
            [contractAddress, parsedAmount]
          );
        }
        const speciesId = resolveSpeciesId(species, speciesList || []);
        if (speciesId === null) {
          return { res: false, message: "UnknownSpecies" };
        }
        await adapter.executeMethod(contractAddress, abi, "depositRevenue", [
          year,
          speciesId,
          parsedAmount,
        ]);
        return { res: true };
      } catch (e) {
        console.log(e);
        return { res: false };
      }
    },
    async callClaimAllAgro(
      { state, commit, dispatch },
      { contractAddress, speciesToBurn, speciesList, projectId }
    ) {
      commit("setIsLoading", true);
      try {
        const adapter = toRaw(state.adapter);
        const provider = await dispatch("callGetProvider");
        const projectAbi = BRAZIL_AGRO_ABI;
        const projectContract = new ethers.Contract(
          contractAddress,
          projectAbi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return { res: false, response: {}, transactionUrl: "" };
        }
        const tokenAbi = AGRO_TOKEN_ABI;
        const tokenContract = new ethers.Contract(
          tokenAddress,
          tokenAbi,
          provider
        );
        const entries = (speciesToBurn && speciesToBurn.length)
          ? speciesToBurn
          : (await dispatch("getToBurnUserAmountAgro", {
              contractAddress,
              projectId,
              speciesList,
            })).normalized;
        const ids = [];
        const amounts = [];
        for (const entry of entries) {
          const tokenId = entry.token_id || entry.tokenId;
          const balanceRaw =
            entry.token_amount_raw ?? entry.balance ?? entry.amount ?? 0;
          if (!tokenId) {
            continue;
          }
          const balance = BigNumber.from(balanceRaw);
          if (balance.isZero()) {
            continue;
          }
          const status = await tokenContract.redemptionStatus(tokenId);
          if (!status.canRedeemRevenue && !status.canRedeemRefund) {
            continue;
          }
          ids.push(BigNumber.from(tokenId).toString());
          amounts.push(balance.toString());
        }
        if (!ids.length) {
          return { res: false, response: {}, transactionUrl: "" };
        }
        const { response } = await adapter.executeMethod(
          tokenAddress,
          tokenAbi,
          "redeemBatch",
          [ids, amounts]
        );
        invalidateAgroBalanceCache({
          projectAddress: contractAddress,
          tokenAddress,
          account: state.currentAccount?.address,
        });
        const hash = response?.hash;
        const networkDetails = Object.values(networkConfig).find(
          (net) => net.chainId === state.networkId.toString()
        );
        let txURL = null;
        if (hash && networkDetails?.blockExplorerUrl) {
          txURL = `${networkDetails.blockExplorerUrl[0]}tx/${hash}`;
        }
        return { res: true, response, transactionUrl: txURL };
      } catch (e) {
        console.log(e);
        return { res: false, response: {}, transactionUrl: "" };
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callClaimInvestment({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callGetYearInfoInvestment({ state: _state, dispatch }, payload) {
      try {
        const abi = BRAZIL_CREDIT_ABI;
        const { contract } = await dispatch("getContractWithoutSigner", {
          contractAddress: payload.contractAddress,
          contractABI: abi,
        });
        const [currentYear, currentStageIndex, projectCompleted, projectFailed] =
          await Promise.all([
            contract.currentBatchYear(),
            contract.currentStageIndex(),
            contract.projectCompleted(),
            contract.projectFailed(),
          ]);
        return {
          currentYear: Number(currentYear),
          currentStageIndex: Number(currentStageIndex),
          nextSignYear: 0,
          nextSignStageIndex: 0,
          nextAuditYear: 0,
          nextAuditStageIndex: 0,
          projectCompleted,
          projectFailed,
        };
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async callGetStageFundingInvestment(
      { state, dispatch },
      { contractAddress, year }
    ) {
      try {
        const provider = await dispatch("callGetProvider");
        const abi = BRAZIL_CREDIT_ABI;
        const contract = new ethers.Contract(contractAddress, abi, provider);
        const stageFunding = [];
        const [costs, prices, raised, signed, audited, withdrawn] =
          await contract.getYearStages(year);
        for (let i = 0; i < costs.length; i++) {
          const cost = BigNumber.from(costs[i]);
          const raisedAmount = BigNumber.from(raised[i]);
          stageFunding.push({
            stage: i,
            cost: ethers.utils.formatUnits(cost, 6),
            raised: ethers.utils.formatUnits(raisedAmount, 6),
            completed: !cost.isZero() && raisedAmount.gte(cost),
            originalYear: year,
            stagePrice: ethers.utils.formatUnits(prices[i], 6),
            audited: audited[i],
            withdrawn: withdrawn[i],
            signed: signed[i],
          });
        }
        return stageFunding;
      } catch (err) {
        console.error("Error fetching stage funding:", err);
        throw err;
      }
    },
    async investWithdraw({ state, dispatch }, payload) {
      const adapter = await ensureWalletAdapter({ state, dispatch });
      const abi = BRAZIL_CREDIT_ABI;
      const methodName = "withdrawFunds";
      await adapter.executeMethod(payload.contractAddress, abi, methodName, [
        payload.year,
        payload.stageIndex,
      ]);
      return true;
    },
    async callGetUserCreditInvestments({ state, dispatch }, payload) {
      if (!payload.projectId) {
        return 0;
      }
      const records = await dispatch("fetchUserRecords");
      const entries = filterUnifiedBalances(records, {
        tokenType: "carbon",
        projectId: payload.projectId,
        year: payload.year,
        tokenAddress: payload.creditContractAddress || payload.tokenAddress,
      });
      const total = entries.reduce(
        (sum, entry) => sum + Number(entry.balance || 0),
        0
      );
      return Number((total / 100).toFixed(2));
    },
    async callGetUserInvestmentsForYear({ state, dispatch }, payload) {
      try {
        if (!payload.projectId) {
          return [];
        }
        const records = await dispatch("fetchUserRecords");
        const entries = filterUnifiedBalances(records, {
          tokenType: "carbon",
          projectId: payload.projectId,
          year: payload.year,
          tokenAddress: payload.creditContractAddress || payload.tokenAddress,
        });
        const grouped = {};
        for (const entry of entries) {
          const stage = Number(entry.stage_idx);
          const amount = Number(entry.balance || 0) / 100;
          grouped[stage] = (grouped[stage] || 0) + amount;
        }
        return Object.entries(grouped).map(([stage, amount]) => ({
          stage: Number(stage),
          amount: Number(amount).toFixed(2),
        }));
      } catch (e) {
        return {};
      }
    },
    async callGetUserYearRate({ state: _state, dispatch }, payload) {
      try {
        const provider = await dispatch("callGetProvider");
        const projectAbi = ABI_REGISTRY.BrazilCarbonCreditUnified;
        const projectContract = new ethers.Contract(
          payload.contractAddress,
          projectAbi.abi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return 0;
        }
        const investmentTokenAbi = ABI_REGISTRY.CarbonCreditInvestmentToken;
        const tokenContract = new ethers.Contract(
          tokenAddress,
          investmentTokenAbi.abi,
          provider
        );
        const rate = await tokenContract.getExchangeRatePercent(payload.year);
        return Number(rate.toString());
      } catch (e) {
        console.log(e);
        return 0;
      }
    },
    async getUserClaimedCreditsForInvest({ state, dispatch }, payload) {
      try {
        if (!payload.projectId) {
          return {
            balance: 0,
            invested: false,
            amount: 0,
            preAmount: 0,
          };
        }
        const records = await dispatch("fetchUserRecords");
        const entries = filterUnifiedBalances(records, {
          tokenType: "carbon",
          projectId: payload.projectId,
          tokenAddress: payload.creditContractAddress || payload.tokenAddress,
        });
        const total = entries.reduce(
          (sum, entry) => sum + Number(entry.balance || 0),
          0
        );
        const invested = total > 0;
        const human = Number((total / 100).toFixed(2));
        return {
          balance: human,
          invested,
          amount: human,
          preAmount: human,
        };
      } catch (e) {
        console.log(e);
        return {
          balance: 0,
          invested: false,
          amount: 0,
          preAmount: 0,
        };
      }
    },
    async getUserBalanceOf({ state, dispatch }, payload) {
      try {
        if (!state.currentAccount.address) {
          return 0;
        }
        if (!payload.projectContractAddress || !payload.year) {
          return { balance: 0 };
        }
        const provider = await dispatch("callGetProvider");
        const saleAsset = isDirectCreditListing(payload.project)
          ? "direct-credit"
          : "claimed-credit";
        const { tokenId, creditTokenAddress } =
          await resolveCarbonSaleToken({
            saleAsset,
            projectAddress: payload.projectContractAddress,
            year: payload.year,
            provider,
            project: payload.project,
          });
        if (!creditTokenAddress || tokenId === undefined) {
          return { balance: 0 };
        }
        const creditToken = new ethers.Contract(
          creditTokenAddress,
          ERC1155_ABI,
          provider
        );
        const balance = await creditToken.balanceOf(
          state.currentAccount.address,
          tokenId
        );
        return {
          balance: balance
            ? ethers.utils.formatUnits(balance.toString(), TOKEN_DECIMALS)
            : 0,
        };
      } catch (e) {
        console.log(e);
        return {
          balance: 0,
          invested: false,
          amount: 0,
          preAmount: 0,
        };
      }
    },
    async getCommissionProject({ state: _state, dispatch: _dispatch }, _payload) {
      return 0;
    },
    async withdrawCommissionProject({ state: _state }, _payload) {
      return false;
    },
    async putOnSell({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async buyFormSale({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async getPurchasedAmount({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.contractAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (!publicOfferingAddress) {
        return { buyerYearlyCredits: {}, buyerCreditsOnSale: {} };
      }
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const contractP = new ethers.Contract(
        publicOfferingAddress,
        brazilCarbonCreditSaleAbi.abi,
        provider
      );
      const buyerYearlyCredits1 = {};
      const buyerCreditsOnSale1 = {};
      const projectAbi = BRAZIL_CREDIT_ABI;
      const projectContract = new ethers.Contract(
        payload.contractAddress,
        projectAbi,
        provider
      );
      const [investmentTokenAddress, stageIdxBn] = await Promise.all([
        projectContract.investmentToken(),
        projectContract.currentStageIndex(),
      ]);
      const investmentAbi = CARBON_INVESTMENT_TOKEN_ABI;
      const investmentToken = new ethers.Contract(
        investmentTokenAddress,
        investmentAbi,
        provider
      );
      for (let year = 1; year <= payload.maxYear; year++) {
        const yearConfig = await projectContract.getYearConfig(year);
        const claimTimestamp = yearConfig.claimTimestamp ?? yearConfig[3];
        const tokenId = await investmentToken.encodeTokenId(
          year,
          Number(stageIdxBn),
          claimTimestamp
        );
        const bought = await investmentToken.balanceOf(
          state.currentAccount.address,
          tokenId
        );
        buyerYearlyCredits1[year] = parseFloat(
          ethers.utils.formatUnits(bought, 2)
        );
        const onSale = await contractP.pendingSales(
          state.currentAccount.address,
          tokenId
        );
        buyerCreditsOnSale1[year] = parseFloat(
          ethers.utils.formatUnits(onSale, 2)
        );
      }
      return {
        buyerYearlyCredits: buyerYearlyCredits1,
        buyerCreditsOnSale: buyerCreditsOnSale1,
      };
    },
    async getUserBalances({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      try {
        const signedIssuanceByYear = payload.project?.signed_issuance_by_year;
        if (!signedIssuanceByYear || typeof signedIssuanceByYear !== "object") {
          return {};
        }
        const userBalances = {};
        const signedYears = Object.keys(signedIssuanceByYear)
          .map((year) => Number(year))
          .filter((year) => Number.isInteger(year) && year > 0)
          .sort((a, b) => a - b);
        for (const year of signedYears) {
          const saleAsset = isDirectCreditListing(payload.project)
            ? "direct-credit"
            : "claimed-credit";
          const { tokenId, creditTokenAddress } = await resolveCarbonSaleToken({
            saleAsset,
            projectAddress: payload.contractAddress,
            year,
            provider,
            project: payload.project,
          });
          const creditToken = new ethers.Contract(
            creditTokenAddress,
            ERC1155_ABI,
            provider
          );
          const balance = await creditToken.balanceOf(
            state.currentAccount.address,
            tokenId
          );
          userBalances[year.toString()] = parseFloat(
            ethers.utils.formatUnits(balance.toString(), 2)
          );
        }
        return userBalances;
      } catch (e) {
        return {};
      }
    },
    async getIsBuyer({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      try {
        const publicOfferingAddress = await resolveOfferingAddress({
          contractAddress: payload.contractAddress,
          provider,
          offeringAddress: payload.offeringAddress,
          project: payload.project,
        });
        if (!publicOfferingAddress) {
          return false;
        }
        const contractP = new ethers.Contract(
          publicOfferingAddress,
          brazilCarbonCreditSaleAbi.abi,
          provider
        );
        if (typeof contractP.buyers !== "function") {
          return false;
        }
        const year =
          payload.year ||
          payload.project?.sale_year ||
          payload.project?.currentInvestmentYear;
        if (year !== undefined && contractP.buyers.length > 1) {
          return await contractP.buyers(state.currentAccount.address, year);
        }
        return await contractP.buyers(state.currentAccount.address);
      } catch (e) {
        return false;
      }
    },
    async getSaleStage({ state, commit, dispatch }, payload) {
      if (payload?.project?.public_offering) {
        return payload?.project?.public_offering.state;
      }
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      try {
        const publicOfferingAddress = await resolveOfferingAddress({
          contractAddress: payload.projectAddress,
          provider,
          offeringAddress: payload.offeringAddress,
          project: payload.project,
        });
        if (!publicOfferingAddress) {
          return 0;
        }
        const contractP = new ethers.Contract(
          publicOfferingAddress,
          brazilCarbonCreditSaleAbi.abi,
          provider
        );
        return await contractP.stage();
      } catch (e) {
        return 0;
      }
    },
    async removeFromSale({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.projectAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (!publicOfferingAddress) {
        return false;
      }
      const adapter = toRaw(state.adapter);
      try {
        let year;
        if (payload.project.public_offering) {
          year = payload.project.sale_year;
        } else {
          year = payload.project.currentInvestmentYear;
        }
        const { tokenId } = await resolveUnifiedCreditSaleToken({
          projectAddress: payload.projectAddress,
          year,
          provider,
        });
        await adapter.executeMethod(
          publicOfferingAddress,
          brazilCarbonCreditSaleAbi.abi,
          "cancelListing",
          [tokenId]
        );
        return true;
      } catch {
        return false;
      }
    },
    async pendingSales({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.projectAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (!publicOfferingAddress) {
        return 0;
      }
      const contractP = new ethers.Contract(
        publicOfferingAddress,
        brazilCarbonCreditSaleAbi.abi,
        provider
      );
      try {
        let year;
        if (payload.year) {
          year = payload.year;
        } else if (payload.project.public_offering) {
          year = payload.project.sale_year;
        } else if (payload.project.currentInvestmentYear) {
          year = payload.project.currentInvestmentYear;
        }
        if (!year) {
          return 0;
        }
        const { tokenId } = await resolveUnifiedCreditSaleToken({
          projectAddress: payload.projectAddress,
          year: Number(year),
          provider,
        });
        return await contractP.pendingSales(
          state.currentAccount.address,
          tokenId
        );
      } catch (e) {
        console.log(e);
        return 0;
      }
    },
      async getAllUserSales({ state, commit, dispatch }, payload) {
        const provider = await dispatch("callGetProvider");
        try {
          const publicOfferingAddress = await resolveOfferingAddress({
            contractAddress: payload.contractAddress,
            provider,
            offeringAddress: payload.offeringAddress,
            project: payload.project,
          });
          if (!publicOfferingAddress) {
            return {};
          }
          const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
          const contractP = new ethers.Contract(
            publicOfferingAddress,
            brazilCarbonCreditSaleAbi.abi,
            provider
          );
          const maxYear = getCarbonTimelineHorizon(payload.project);
          if (!maxYear) {
            return {};
          }
          const userSales = {};
          for (let year = 1; year <= maxYear; year++) {
            const { tokenId } = await resolveUnifiedCreditSaleToken({
              projectAddress: payload.contractAddress,
              year,
              provider,
            });
            const onSale = await contractP.pendingSales(
              state.currentAccount.address,
              tokenId
            );
            userSales[year.toString()] = parseFloat(
              ethers.utils.formatUnits(onSale.toString(), 2)
            );
          }
          return userSales;
      } catch {
        return {};
      }
    },
    async getUserYearIssuedCredits(
      { state: _state, commit: _commit, dispatch },
      payload
    ) {
      try {
        if (!payload.projectId) {
          return {};
        }
        const records = await dispatch("fetchUserRecords");
        const entries = filterUnifiedBalances(records, {
          tokenType: "carbon",
          projectId: payload.projectId,
          tokenAddress: payload.creditContractAddress || payload.tokenAddress,
        });
        const byYear = {};
        for (const entry of entries) {
          const year = Number(entry.year);
          const amount = Number(entry.balance || 0) / 100;
          byYear[year] = (byYear[year] || 0) + amount;
        }
        return byYear;
      } catch {
        return {};
      }
    },
    async getYearlyTotalAvailable({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.contractAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (!publicOfferingAddress) {
        return "0";
      }
      const contractP = new ethers.Contract(
        publicOfferingAddress,
        brazilCarbonCreditSaleAbi.abi,
        provider
      );
      const saleAsset = isDirectCreditListing(payload.project)
        ? "direct-credit"
        : "claimed-credit";
      const { tokenId } = await resolveCarbonSaleToken({
        saleAsset,
        projectAddress: payload.contractAddress,
        year: Number(payload.year),
        provider,
        project: payload.project,
      });
      const total = await contractP.tokenTotalAvailable(tokenId);
      return ethers.utils.formatUnits(total.toString(), 2);
    },
    async blacklistDeveloper({ state }, payload) {
      try {
        const adapter = toRaw(state.adapter);
        const contractAddress =
          state.networkSettings.agroforestryProjectFactoryContract;
        const contractABI = ABI_REGISTRY.AgroforestryProjectFactory;
        const methodName = "blockDeveloper";
        await adapter.executeMethod(
          contractAddress,
          contractABI.abi,
          methodName,
          [payload.developerAddress]
        );
        return true;
      } catch {
        return false;
      }
    },
    async removeDeveloperFromBlacklist({ state }, payload) {
      try {
        const adapter = toRaw(state.adapter);
        const contractAddress =
          state.networkSettings.agroforestryProjectFactoryContract;

        const contractABI = ABI_REGISTRY.AgroforestryProjectFactory;
        const methodName = "unblockDeveloper";
        await adapter.executeMethod(
          contractAddress,
          contractABI.abi,
          methodName,
          [payload.developerAddress]
        );
        return true;
      } catch {
        return false;
      }
    },
    async getSaleCommission({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.contractAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (
        !publicOfferingAddress ||
        publicOfferingAddress === "0x0000000000000000000000000000000000000000"
      )
        return;
      const contractP = new ethers.Contract(
        publicOfferingAddress,
        brazilCarbonCreditSaleAbi.abi,
        provider
      );
      let usdCommissionAmount = 0;
      try {
        const factoryAddress =
          state.networkSettings.agroforestryProjectFactoryContract;
        const factoryAbi = ABI_REGISTRY.AgroforestryProjectFactory;
        const factoryContract = new ethers.Contract(factoryAddress, factoryAbi.abi, provider);
        const isFunding = await factoryContract.isFundingAddress(state.currentAccount.address);
        if (isFunding) {
          usdCommissionAmount = await contractP.usdCommissionAmount();
        }
      } catch (e) {
        console.log(e);
      }
      return usdCommissionAmount;
    },
    async withdrawUsdcCommission({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const brazilCarbonCreditSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const publicOfferingAddress = await resolveOfferingAddress({
        contractAddress: payload.contractAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
      if (!publicOfferingAddress) {
        return;
      }
      const adapter = toRaw(state.adapter);
      const methodName = "withdrawCommission";
      await adapter.executeMethod(
        publicOfferingAddress,
        brazilCarbonCreditSaleAbi.abi,
        methodName
      );
    },
    async getPublicOfferingAddress({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      return resolveOfferingAddress({
        contractAddress: payload.contractAddress,
        provider,
        offeringAddress: payload.offeringAddress,
        project: payload.project,
      });
    },
    async updatePrice({ state, dispatch }, payload) {
      const newPrice = ethers.utils.parseUnits(payload.price.toString(), 2);
      const adapter = toRaw(state.adapter);
      const contractAddress = payload.contractAddress;
      const publicSaleAbi = ABI_REGISTRY.CarbonCreditPublicSale;
      const provider =
        adapter.provider || (await dispatch("callGetProvider"));
      const { tokenId } = await resolveUnifiedCreditSaleToken({
        projectAddress: payload.projectAddress || payload.contractAddress,
        year: payload.year,
        provider,
      });
      await adapter.executeMethod(
        contractAddress,
        publicSaleAbi.abi,
        "changePrice",
        [tokenId, newPrice]
      );
    },
    async changeSaleState({ state }, payload) {
      try {
        const contractAddress = payload.contractAddress;

        const contractABI = ABI_REGISTRY.CarbonCreditPublicSale;
        const newState = Number(payload.newState);
        let methodName = "";
        if (newState === 1) {
          methodName = "start";
        } else if (newState === 2) {
          methodName = "end";
        } else if (newState === 3) {
          methodName = "pause";
        }
        const adapter = toRaw(state.adapter);
        await adapter.executeMethod(
          contractAddress,
          contractABI.abi,
          methodName
        );
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async getContractWithoutSigner({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const contract = new ethers.Contract(
        payload.contractAddress,
        payload.contractABI,
        provider
      );
      return { contract, provider };
    },
    async getUserBCTBalance({ state, commit, dispatch }) {
      try {
        const contractAddress = state.networkSettings?.bctTokenContract;
        if (!contractAddress) {
          commit("setBctBalance", "0");
          return 0;
        }
        const provider = await dispatch("callGetProvider");
        const code = await provider.getCode(contractAddress);
        if (!code || code === "0x") {
          console.log("[BCT-BALANCE] skipped: no deployed BCT contract", {
            contractAddress,
            networkId: state.networkId,
          });
          commit("setBctBalance", "0");
          return 0;
        }
        const bct = new ethers.Contract(
          contractAddress,
          USDC_ABI,
          provider
        );
        const balance = await bct.balanceOf(state.currentAccount.address);
        const parsedBalance = ethers.utils.formatUnits(balance, 18);
        commit("setBctBalance", parsedBalance ?? 0);
        return parsedBalance;
      } catch (error) {
        console.log("[BCT-BALANCE] read failed", {
          message: error?.message || String(error),
          contractAddress: state.networkSettings?.bctTokenContract || null,
          networkId: state.networkId,
        });
        commit("setBctBalance", "0");
        return 0;
      }
    },
    async callMintAgroforestryProject({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        console.log("[callMintAgroforestryProject] Deploying agro project...");
        const networkSettings = await dispatch(
          "getNetworkSettings",
          state.networkId
        );
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const contractAddress =
          networkSettings.agroforestryProjectFactoryContract;
        const carbonV2FactoryAddress = networkSettings.carbonV2FactoryContract;
        if (
          contractAddress &&
          carbonV2FactoryAddress &&
          ethers.utils.isAddress(contractAddress) &&
          ethers.utils.isAddress(carbonV2FactoryAddress) &&
          contractAddress.toLowerCase() === carbonV2FactoryAddress.toLowerCase()
        ) {
          throw new Error(
            "Agro factory misconfigured: agroforestryProjectFactoryContract points to the Carbon V2 factory."
          );
        }
        const { contractArgs } = payload;
        const maxProjectYears = Number(payload.maxProjectYears);
        const speciesCount = Number(payload.speciesCount);
        const yearStageCounts = contractArgs.yearStageCounts;
        const usdcTreasuryAddress = payload.usdcTreasuryAddress;
        const tokenUri = payload.tokenUri;

        const args = [
          networkSettings.usdcTokenContract,
          state.currentAccount.address,
          payload.auditor,
          usdcTreasuryAddress,
          maxProjectYears,
          speciesCount,
          yearStageCounts,
          tokenUri,
          contractArgs.costs,
          contractArgs.prices,
          contractArgs.predictedRevenues,
          contractArgs.claimTimestamps,
        ];
        const { receipt } = await adapter.executeMethod(
          contractAddress,
          AGRO_FACTORY_ACTIVE_ABI,
          "createAgroforestryProject",
          args,
          localFactoryTxOptions(state.networkId, LOCAL_FACTORY_CREATE_GAS_LIMIT)
        );

        const iface = new ethers.utils.Interface(AGRO_FACTORY_ACTIVE_ABI);
        const createdLog = receipt.logs
          .map((log) => { try { return iface.parseLog(log); } catch { return null; } })
          .find((parsed) => parsed?.name === "ProjectCreated");
        console.log("[callMintAgroforestryProject] event parsing result:", createdLog ? "ProjectCreated found" : "ProjectCreated NOT found");
        if (!createdLog) {
          throw new Error("ProjectCreated event not found in transaction receipt.");
        }
        const { projectAddress, tokenAddress } =
          extractProjectCreatedAddresses(createdLog);

        return {
          project_address: projectAddress,
          token_address: tokenAddress,
        };
      } catch (err) {
        console.log(err);
        const extractedMessage = extractMetamaskErrorMessage(err);
        const userMessage = normalizeAgroMintErrorMessage(extractedMessage);
        commit("setWalletErrorMessage", userMessage);
        throw new Error(userMessage);
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callMintCarbonProject({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callSignCreditStageUnified({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callApproveAgroProject({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const networkSettings = await dispatch(
          "getNetworkSettings",
          state.networkId
        );
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const contractAddress =
          networkSettings.agroforestryProjectFactoryContract;
        const contractABI = BRAZIL_FACTORY_ABI;
        const methodName = "approveProject";
        const args = [payload.projectAddress];
        const result = await adapter.executeMethod(
          contractAddress,
          contractABI,
          methodName,
          args
        );
        return { txHash: result?.response?.hash };
      } catch (err) {
        console.log(err);
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callApproveCarbonProject({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const networkSettings = await dispatch(
          "getNetworkSettings",
          state.networkId
        );
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const contractAddress =
          networkSettings.agroforestryProjectFactoryContract;
        const contractABI = BRAZIL_FACTORY_ABI;
        const result = await adapter.executeMethod(
          contractAddress,
          contractABI,
          "approveProject",
          [payload.projectAddress]
        );
        return { txHash: result?.response?.hash };
      } catch (err) {
        console.log(err);
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callStartPublicCreditSale({ state, commit, dispatch }, payload) {
      commit("setIsLoading", true);
      try {
        const adapter = await ensureWalletAdapter({ state, dispatch });
        const saleAbi = ABI_REGISTRY.CarbonCreditPublicSale.abi;
        const result = await adapter.executeMethod(
          payload.saleAddress,
          saleAbi,
          "start",
          []
        );
        return { txHash: result?.response?.hash };
      } catch (err) {
        const extractedMessage = extractMetamaskErrorMessage(err);
        commit("setWalletErrorMessage", extractedMessage);
        throw err;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async callGetYearInfoAgroInvestment(
      { state: _state, commit: _commit, dispatch },
      { contractAddress: _contractAddress, project, refetch: _refetch }
    ) {
      try {
        // If project already has indexed state, return it directly (skip RPC)
        if (
          !_refetch &&
          project?.currentYear != null &&
          project?.currentStageIndex != null
        ) {
          return {
            currentYear: project.currentYear,
            currentStageIndex: project.currentStageIndex,
            projectCompleted: project.projectCompleted ?? false,
            projectFailed: project.projectFailed ?? false,
            nextSignYear: project.nextSignYear ?? 0,
            nextSignStageIndex: project.nextSignStageIndex ?? 0,
            nextAuditYear: project.nextAuditYear ?? 0,
            nextAuditStageIndex: project.nextAuditStageIndex ?? 0,
          };
        }

        let backendData = null;
        try {
          const response = await axios.get(
            getURLFromPieces(AGROFORESTRY_PROJECT_ENDPOINT, project.id)
          );
          backendData = response.data || null;
        } catch (e) {
          backendData = null;
        }

        const contractAddress =
          _contractAddress ||
          project?.smart_contract_address ||
          null;
        let chainData = null;
        if (contractAddress) {
          try {
            const provider = await dispatch("callGetProvider");
            const abi = BRAZIL_AGRO_ABI;
            const agroContract = new ethers.Contract(
              contractAddress,
              abi,
              provider
            );
            const [
              nextAuditYear,
              nextAuditStageIndex,
              projectCompleted,
              projectFailed,
            ] = await Promise.all([
              agroContract.nextAuditYear(),
              agroContract.nextAuditStageIndex(),
              agroContract.projectCompleted(),
              agroContract.projectFailed(),
            ]);
            chainData = {
              nextAuditYear: nextAuditYear.toNumber
                ? nextAuditYear.toNumber()
                : nextAuditYear,
              nextAuditStageIndex: nextAuditStageIndex.toNumber
                ? nextAuditStageIndex.toNumber()
                : nextAuditStageIndex,
              projectCompleted,
              projectFailed,
            };
          } catch (e) {
            chainData = null;
          }
        }

        if (!backendData && !chainData) {
          return null;
        }

        return {
          currentYear: backendData?.currentYear,
          currentStageIndex: backendData?.currentStageIndex,
          projectCompleted:
            chainData?.projectCompleted ?? backendData?.projectCompleted,
          projectFailed:
            chainData?.projectFailed ?? backendData?.projectFailed,
          nextSignYear: backendData?.nextSignYear,
          nextSignStageIndex: backendData?.nextSignStageIndex,
          nextAuditYear:
            chainData?.nextAuditYear ?? backendData?.nextAuditYear,
          nextAuditStageIndex:
            chainData?.nextAuditStageIndex ?? backendData?.nextAuditStageIndex,
        };
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async callFarmerWithdrawAgroInvest({ state, dispatch }, payload) {
      const adapter = await ensureWalletAdapter({ state, dispatch });
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "withdrawFunds";
      const args = [payload.year, payload.stageIndex];
      await adapter.executeMethod(
        payload.contractAddress,
        abi,
        methodName,
        args
      );
    },
    async callFarmerSignAgroStage({ state, dispatch }, payload) {
      const adapter = await ensureWalletAdapter({ state, dispatch });
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "signStage";
      const args = [];
      await adapter.executeMethod(
        payload.contractAddress,
        abi,
        methodName,
        args
      );
    },
    async callFarmerFinalizeSpecies({ state, dispatch }, payload) {
      const adapter = await ensureWalletAdapter({ state, dispatch });
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "finalizeSpecies";
      const speciesId = resolveSpeciesId(
        payload.species,
        payload.speciesList || []
      );
      if (speciesId === null) {
        throw new Error("Unknown species");
      }
      const args = [payload.year, speciesId];
      await adapter.executeMethod(
        payload.contractAddress,
        abi,
        methodName,
        args
      );
    },
    async getAgroFinalizedBySpeciesYear({ dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const projectAbi = BRAZIL_AGRO_ABI;
      const projectContract = new ethers.Contract(
        payload.contractAddress,
        projectAbi,
        provider
      );
      const speciesList = normalizeSpeciesList(payload.speciesList);
      const maxYears = Number(payload.maxYears || 0);
      if (!maxYears || !speciesList.length) {
        return {};
      }
      const rawYears = Array.isArray(payload.years) ? payload.years : [];
      const years = rawYears.length
        ? Array.from(
            new Set(
              rawYears
                .map((y) => Number(y))
                .filter(
                  (y) => Number.isFinite(y) && y >= 1 && y <= maxYears
                )
            )
          ).sort((a, b) => a - b)
        : Array.from({ length: maxYears }, (_, i) => i + 1);
      const result = {};
      await Promise.all(
        years.map(async(year) =>{
          result[year] = {};
          for (let speciesId = 0; speciesId < speciesList.length; speciesId += 1) {
              const config = await projectContract.getSpeciesConfig(year, speciesId);
              const finalized = config.finalized ?? config[5] ?? false;
              const key = speciesList[speciesId] ?? String(speciesId);
              result[year][key] = !!finalized;
            }
          }
        )
      );
      return result;
    },
    async callFarmerUpdateProduction({ state, dispatch }, payload) {
      const adapter = await ensureWalletAdapter({ state, dispatch });
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "recordHarvest";

      try {
        for (const r of payload.rows || []) {
          if (!r.harvested) continue;

          const yearOnChain = toOnChainYear(r.year, payload.baseYear);
          const speciesId = resolveSpeciesId(
            r.species,
            payload.speciesList || []
          );
          if (speciesId === null) {
            continue;
          }
          const quantity = ethers.utils.parseUnits(r.amount, 2);
          const pricePerUnit = ethers.utils.parseUnits(r.price, 6);
          const args = [
            Number(yearOnChain).toString(),
            speciesId,
            quantity,
            pricePerUnit,
            quantity.mul(pricePerUnit).div(100),
          ];

          try {
            await adapter.executeMethod(
              payload.contractAddress,
              abi,
              methodName,
              args
            );
          } catch (e) {
            const msg = String(e?.error?.message || e?.message || "");
            if (!msg.includes("HARVEST_ALREADY_REPORTED")) {
              throw e;
            }
          }
        }
        return true;
      } catch (err) {
        console.error("callFarmerUpdateProduction error:", err);
        return false;
      }
    },
    // REMOVED: callSetHarvestedFarmer — harvests tracked by indexer via HarvestRecorded events
    async callGetStageFundingAgroInvestment(
      { state, commit, dispatch },
      { contractAddress, year }
    ) {
      try {
        const provider = await dispatch("callGetProvider");
        const abi = BRAZIL_AGRO_ABI;
        const contract = new ethers.Contract(contractAddress, abi, provider);
        const funding = [];
        const [costs, prices, raised, feesCollected, signed, audited, withdrawn] =
          await contract.getYearStages(year);
        for (let i = 0; i < costs.length; i++) {
          const cost = costs[i];
          const price = prices[i];
          const raisedAmount = raised[i];
          const feeCollected = feesCollected[i];
          const costBn = BigNumber.from(cost);
          const raisedBn = BigNumber.from(raisedAmount);
          const decoded = {
            cost,
            price,
            raised: raisedAmount,
            raisedAfterCommission: BigNumber.from(raisedAmount).sub(feeCollected),
            completed: !costBn.isZero() && raisedBn.gte(costBn),
            signed: signed[i],
            audited: audited[i],
            withdrawn: withdrawn[i],
          };
          funding.push({
            stage: i,
            cost: ethers.utils.formatUnits(decoded.cost, 6),
            stagePrice: ethers.utils.formatUnits(decoded.price, 6),
            raised: ethers.utils.formatUnits(decoded.raised, 6),
            raisedAfterCommission: ethers.utils.formatUnits(
              decoded.raisedAfterCommission,
              6
            ),
            completed: decoded.completed,
            signed: decoded.signed,
            audited: decoded.audited,
            withdrawn: decoded.withdrawn,
            year: year.toString(),
            originalYear: year.toString(),
          });
        }
        return funding;
      } catch (err) {
        console.log(err);
        throw err;
      }
    },

    async callGetUserAgroCreditInvestments(
      { state, dispatch },
      { contractAddress, year, projectId: _projectId }
    ) {
      try {
        if (!state.currentAccount?.address) {
          return "0";
        }
        const provider = await dispatch("callGetProvider");
        const projectAbi = BRAZIL_AGRO_ABI;
        const projectContract = new ethers.Contract(
          contractAddress,
          projectAbi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return "0";
        }
        const tokenContract = new ethers.Contract(
          tokenAddress,
          AGRO_TOKEN_ABI,
          provider
        );
        let balances = await getUnifiedAgroBalances({
          projectContract,
          tokenContract,
          account: state.currentAccount.address,
          projectAddress: contractAddress,
          tokenAddress,
        });
        const yearBalances = balances.filter(
          (entry) => Number(entry.year) === Number(year)
        );
        if (!yearBalances.length) {
          return "0";
        }
        const total = yearBalances.reduce(
          (sum, entry) => sum.add(entry.balance),
          BigNumber.from(0)
        );
        return Number(ethers.utils.formatUnits(total, 2)).toFixed(2);
      } catch (e) {
        console.error(e);
        return "0";
      }
    },

    async callGetUserAgroInvestmentsForYear(
      { state, dispatch },
      { contractAddress, year, projectId: _projectId }
    ) {
      try {
        if (!state.currentAccount?.address) {
          return [];
        }
        const provider = await dispatch("callGetProvider");
        const projectAbi = BRAZIL_AGRO_ABI;
        const projectContract = new ethers.Contract(
          contractAddress,
          projectAbi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return [];
        }
        const tokenContract = new ethers.Contract(
          tokenAddress,
          AGRO_TOKEN_ABI,
          provider
        );
        let balances = await getUnifiedAgroBalances({
          projectContract,
          tokenContract,
          account: state.currentAccount.address,
          projectAddress: contractAddress,
          tokenAddress,
        });
        const yearBalances = balances.filter(
          (entry) => Number(entry.year) === Number(year)
        );
        if (!yearBalances.length) {
          return [];
        }
        const totals = {};
        yearBalances.forEach((entry) => {
          if (!entry.balance || entry.balance.isZero()) {
            return;
          }
          const key = String(entry.speciesId);
          if (!totals[key]) {
            totals[key] = BigNumber.from(0);
          }
          totals[key] = totals[key].add(entry.balance);
        });
        return Object.entries(totals)
          .filter(([, amount]) => !amount.isZero())
          .map(([species, amount]) => ({
            species: Number(species),
            amount: parseFloat(
              Number(ethers.utils.formatUnits(amount, 2)).toFixed(2)
            ),
          }));
      } catch (e) {
        console.error("callGetUserAgroInvestmentsForYear error", e);
        return [];
      }
    },

    async callGetUserAgroYearRate(
      { state: _state, commit: _commit, dispatch: _dispatch },
      {
        contractAddress: _contractAddress,
        year: _year,
        projectId: _projectId,
        productionBySpecies: _productionBySpecies,
        speciesList: _speciesList,
      }
    ) {
      try {
        return 0;
      } catch (e) {
        console.error(e);
        return 0;
      }
    },
    async callInvestAgroforestry({ state, commit }, payload) {
      try {
        commit("setIsLoading", true);
        const adapter = toRaw(state.adapter);
        const abi = AGRO_PROJECT_INVEST_ABI;
        const methodName = "invest";
        const parsedAmount = ethers.utils.parseUnits(
          payload.amount.toString(),
          6
        );

        const usdcContract = new ethers.Contract(
          state.networkSettings.usdcTokenContract,
          USDC_ABI,
          adapter.signer
        );
        const userUSDCBalance = await usdcContract.balanceOf(
          state.currentAccount.address
        );
        if (userUSDCBalance.lt(parsedAmount)) {
          commit("setWalletErrorMessage", "Not enough USDC balance.");
          return { res: false };
        }

        const allowance = await usdcContract.allowance(
          state.currentAccount.address,
          payload.contractAddress
        );
        if (allowance.lt(parsedAmount)) {
          await adapter.executeMethod(
            state.networkSettings.usdcTokenContract,
            USDC_ABI,
            "approve",
            [payload.contractAddress, parsedAmount]
          );
        }

        const args = [parsedAmount];
          const { response } = await adapter.executeMethod(
            payload.contractAddress,
            abi,
            methodName,
            args
          );
          invalidateAgroBalanceCache({
            projectAddress: payload.contractAddress,
            account: state.currentAccount?.address,
          });

        const hash = response.hash;
        const networkDetails = Object.values(networkConfig).find(
          (net) => net.chainId === state.networkId.toString()
        );
        let txUrl = null;
        if (networkDetails && networkDetails.blockExplorerUrl) {
          txUrl = `${networkDetails.blockExplorerUrl[0]}tx/${hash}`;
        }

        return { txUrl, response };
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        commit("setIsLoading", false);
      }
    },
    async getFarmerSignStageInfo({ state, commit, dispatch }, payload) {
      if(payload.id){
        const response = await axios.get(
          getURLFromPieces(AGROFORESTRY_PROJECT_ENDPOINT, payload.id)
        );
        if (response.data) {
          return {
            projectCompleted: response.data.projectCompleted,
            nextSignYear: response.data.nextSignYear,
            nextSignStageIndex: response.data.nextSignStageIndex,
          };
        }
      }
      const provider = await dispatch("callGetProvider");
      const abi = BRAZIL_AGRO_ABI;
      const agroInvestmentContract = new ethers.Contract(
        payload.contractAddress,
        abi,
        provider
      );

      const [nextSignYear, nextSignStageIndex, projectCompleted] =
        await Promise.all([
          agroInvestmentContract.nextSignYear(),
          agroInvestmentContract.nextSignStageIndex(),
          agroInvestmentContract.projectCompleted(),
        ]);

      return {
        nextSignYear: nextSignYear.toNumber ?  nextSignYear.toNumber() : nextSignYear,
        nextSignStageIndex: nextSignStageIndex.toNumber ? nextSignStageIndex.toNumber() : nextSignStageIndex,
        projectCompleted,
      };
    },
    async auditNextStageAgro({ state }, payload) {
      const adapter = toRaw(state.adapter);
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "auditStage";
      const args = [];
        await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName,
          args
        );
    },
    async auditNextStageCredit({ state }, payload) {
      const adapter = toRaw(state.adapter);
      const abi = BRAZIL_CREDIT_ABI;
      const methodName = "auditStage";
      const args = [];
      await adapter.executeMethod(
        payload.contractAddress,
        abi,
        methodName,
        args
      );
    },
    async auditNextStageAgroFailed({ state }, payload) {
      const adapter = toRaw(state.adapter);
      const abi = BRAZIL_AGRO_ABI;
      const methodName = "markProjectFailed";
      const args = [];
        await adapter.executeMethod(
          payload.contractAddress,
          abi,
          methodName,
          args
        );
    },
    // markProjectFailed() is factory-only — auditors use pauseForUnsuccessfulAudit() instead
    // (dispatched via callPauseAuditFunction)
    async callGetProjectFailedAgro({ dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const abi = BRAZIL_AGRO_ABI;
      const contract = new ethers.Contract(
        payload.contractAddress,
        abi,
        provider
      );
      return await contract.projectFailed();
    },
    async callGetProjectFailedCredit({ dispatch }, payload) {
      try {
        const provider = await dispatch("callGetProvider");
        const abi = BRAZIL_CREDIT_ABI;
        const contract = new ethers.Contract(
          payload.contractAddress,
          abi,
          provider
        );
        return await contract.projectFailed();
      } catch {
        return false;
      }
    },
    async callGetUserRefundAmountAgro(
      { state, commit, dispatch },
      { contractAddress, year, speciesList }
    ) {
      if (!state.currentAccount?.address) {
        return "0";
      }
      if (!year || Number(year) <= 0) {
        return "0";
      }
      if (!Array.isArray(speciesList) || speciesList.length === 0) {
        return "0";
      }
      const provider = await dispatch("callGetProvider");
      const abi = BRAZIL_AGRO_ABI;
      const projectContract = new ethers.Contract(contractAddress, abi, provider);
      const burnAmount = await projectContract.getToBurnByUserAllSpecies(
        state.currentAccount.address,
        Number(year)
      );
      if (BigNumber.from(burnAmount ?? 0).isZero()) {
        return "0";
      }
      const usdcAmount = await projectContract.getRefundableByUserAllSpecies(
        state.currentAccount.address,
        Number(year)
      );
      return ethers.utils.formatUnits(usdcAmount, 6);
    },
    async callGetUserRefundAmountCredit({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callGetUserContributions(
      { state, commit: _commit, dispatch },
      { contractAddress, year, projectId: _projectId }
    ) {
      try {
        if (!state.currentAccount?.address) {
          return 0;
        }
        const provider = await dispatch("callGetProvider");
        const projectAbi = BRAZIL_AGRO_ABI;
        const projectContract = new ethers.Contract(
          contractAddress,
          projectAbi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return 0;
        }
        const tokenContract = new ethers.Contract(
          tokenAddress,
          AGRO_TOKEN_ABI,
          provider
        );
        let balances = await getUnifiedAgroBalances({
          projectContract,
          tokenContract,
          account: state.currentAccount.address,
          projectAddress: contractAddress,
          tokenAddress,
        });
        const yearBalances = balances.filter(
          (entry) => Number(entry.year) === Number(year)
        );
        if (!yearBalances.length) {
          return 0;
        }
        const total = yearBalances.reduce(
          (sum, entry) => sum.add(entry.balance),
          BigNumber.from(0)
        );
        return parseFloat(
          Number(ethers.utils.formatUnits(total, 2)).toFixed(2)
        );
      } catch (e) {
        console.log(e);
        return 0;
      }
    },
    async callGetUserContributionsCredit(
      { state: _state, commit: _commit, dispatch },
      {
        contractAddress,
        year,
        investmentId: _investmentId,
        projectId,
        creditContractAddress,
      }
    ) {
      try {
        if (!projectId) {
          return 0;
        }
        const records = await dispatch("fetchUserRecords");
        const entries = filterUnifiedBalances(records, {
          tokenType: "carbon",
          projectId,
          year,
          tokenAddress: creditContractAddress || contractAddress,
        });
        const total = entries.reduce(
          (sum, entry) => sum + Number(entry.balance || 0),
          0
        );
        return Number((total / 100).toFixed(2));
      } catch (e) {
        console.log(e);
        return 0;
      }
    },
    async getAdminAuditStageInfo({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      commit("setProvider", provider);
      const abi = BRAZIL_AGRO_ABI;
      const agroContract = new ethers.Contract(
        payload.contractAddress,
        abi,
        provider
      );
      const [nextAuditYear, nextAuditStageIndex, projectCompleted, projectFailed] =
        await Promise.all([
          agroContract.nextAuditYear(),
          agroContract.nextAuditStageIndex(),
          agroContract.projectCompleted(),
          agroContract.projectFailed(),
        ]);
      return {
        nextAuditYear: nextAuditYear.toString(),
        nextAuditStageIndex: nextAuditStageIndex.toString(),
        projectFailed,
        projectCompleted,
      };
    },
    async getAgroTotalInvested({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const abi = BRAZIL_AGRO_ABI;
      const agroInvestmentContract = new ethers.Contract(
        payload.contractAddress,
        abi,
        provider
      );
      const toNumberSafe = (value) =>
        ethers.BigNumber.isBigNumber(value) ? value.toNumber() : Number(value);
      const maxYear = toNumberSafe(await agroInvestmentContract.maxProjectYear());
      const totals = [];
      for (let year = 1; year <= maxYear; year++) {
        let yearRaised = ethers.BigNumber.from(0);
        const yearStages = await agroInvestmentContract.getYearStages(year);
        const stageCount = toNumberSafe(
          await agroInvestmentContract.stageCountForYear(year)
        );
        for (let idx = 0; idx < stageCount; idx++) {
          yearRaised = yearRaised.add(yearStages.raised[idx]);
        }
        totals.push(ethers.utils.formatUnits(yearRaised, 6));
      }
      return totals;
    },
    async getAgroInvestStageInfo({ state, commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const abi = BRAZIL_AGRO_ABI;
      const agroContract = new ethers.Contract(
        payload.contractAddress,
        abi,
        provider
      );
      const info = await agroContract.getStageInfo(
        payload.year,
        payload.stageIndex
      );
      const raisedBn = BigNumber.from(info.raised ?? info[2] ?? 0);
      const feeCollectedBn = BigNumber.from(info.feeCollected ?? info[3] ?? 0);
      const raisedAfterCommissionBn = raisedBn.sub(feeCollectedBn);
      return {
        stage: payload.stageIndex,
        cost: ethers.utils.formatUnits(info.cost ?? info[0], 6),
        stagePrice: ethers.utils.formatUnits(info.price ?? info[1], 6),
        raised: ethers.utils.formatUnits(raisedBn, 6),
        raisedAfterCommission: ethers.utils.formatUnits(
          raisedAfterCommissionBn,
          6
        ),
        completed: info.signed ?? info[3],
        signed: info.signed ?? info[3],
        audited: info.audited ?? info[4],
        withdrawn: info.withdrawn ?? info[5],
      };
    },
    async getUserSpeciesAgroforestry({ state, dispatch }, payload) {
      if (!state.currentAccount?.address) {
        return {};
      }
      const provider = await dispatch("callGetProvider");
      const projectAbi = BRAZIL_AGRO_ABI;
      const projectContract = new ethers.Contract(
        payload.contractAddress,
        projectAbi,
        provider
      );
      const tokenAddress = await projectContract.investmentToken();
      if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
        return {};
      }
      const tokenContract = new ethers.Contract(
        tokenAddress,
        AGRO_TOKEN_ABI,
        provider
      );
      let balances = await getUnifiedAgroBalances({
        projectContract,
        tokenContract,
        account: state.currentAccount.address,
        projectAddress: payload.contractAddress,
        tokenAddress,
      });
      const totals = {};
      balances.forEach((entry) => {
        if (!entry.balance || entry.balance.isZero()) {
          return;
        }
        const year = Number(entry.year);
        if (payload.maxYears && year > payload.maxYears) {
          return;
        }
        const speciesId = entry.speciesId;
        if (!totals[year]) {
          totals[year] = {};
        }
        if (!totals[year][speciesId]) {
          totals[year][speciesId] = BigNumber.from(0);
        }
        totals[year][speciesId] = totals[year][speciesId].add(entry.balance);
      });

      const savedSpecies = {};
      Object.keys(totals).forEach((year) => {
        savedSpecies[year] = {};
        Object.keys(totals[year]).forEach((speciesId) => {
          savedSpecies[year][speciesId] = parseFloat(
            Number(ethers.utils.formatUnits(totals[year][speciesId], 2)).toFixed(2)
          );
        });
      });
      return savedSpecies;
    },
    async callAgroRefund({ state, dispatch }, payload) {
      try {
        const { res, transactionUrl } = await dispatch("callClaimAllAgro", {
          contractAddress: payload.contractAddress,
          speciesToBurn: payload.speciesToBurn,
          speciesList: payload.speciesList,
          projectId: payload.projectId,
        });
        return { res, transactionUrl };
      } catch (e) {
        console.log(e);
        return { res: false, transactionUrl: null };
      }
    },
    async callCreditRefund({ commit }) {
      return unsupportedCarbonRuntime(commit);
    },
    async callGetUserAgroClaim({ state, commit: _commit, dispatch }, payload) {
      const provider = await dispatch("callGetProvider");
      const projectAbi = BRAZIL_AGRO_ABI;
      const projectContract = new ethers.Contract(
        payload.contractAddress,
        projectAbi,
        provider
      );
      const tokenAddress = await projectContract.investmentToken();
      if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
        return 0;
      }
      const tokenContract = new ethers.Contract(
        tokenAddress,
        AGRO_TOKEN_ABI,
        provider
      );
      if (!state.currentAccount?.address) {
        return 0;
      }
      let balances = await getUnifiedAgroBalances({
        projectContract,
        tokenContract,
        account: state.currentAccount.address,
        projectAddress: payload.contractAddress,
        tokenAddress,
        forceRefresh: true,
      });
      // Group by claimYear-speciesId (not investYear-speciesId)
      const grouped = new Map();
      balances.forEach((entry) => {
        if (!entry.balance || entry.balance.isZero()) {
          return;
        }
        if (!Number.isInteger(Number(entry.claimYear))) {
          throw new Error("Agro claim balance is missing claimYear.");
        }
        const claimYr = Number(entry.claimYear);
        const key = `${claimYr}-${entry.speciesId}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            year: claimYr,
            speciesId: entry.speciesId,
            tokenId: entry.tokenId,
            balance: BigNumber.from(0),
          });
        }
        grouped.get(key).balance = grouped.get(key).balance.add(entry.balance);
      });

      let total = ethers.BigNumber.from(0);
      for (const entry of grouped.values()) {
        const status = await tokenContract.redemptionStatus(entry.tokenId);
        if (!status.canRedeemRevenue) {
          continue;
        }
        const amount = await tokenContract.calculatePayout(
          entry.year,
          entry.speciesId,
          entry.balance
        );
        total = total.add(amount);
      }
      return parseFloat(Number(ethers.utils.formatUnits(total, 6)).toFixed(2));
    },
      async getToBurnUserAmountAgro({ state, dispatch }, payload) {
        const provider = await dispatch("callGetProvider");
        const projectAbi = BRAZIL_AGRO_ABI;
        const projectContract = new ethers.Contract(
          payload.contractAddress,
          projectAbi,
          provider
        );
        const tokenAddress = await projectContract.investmentToken();
        if (!tokenAddress || tokenAddress === ethers.constants.AddressZero) {
          return { normalized: [], byYear: {} };
        }
        const tokenContract = new ethers.Contract(
          tokenAddress,
          AGRO_TOKEN_ABI,
          provider
        );
        const normalized = [];
        const byYear = {};
        if (!Array.isArray(payload.speciesList)) {
          throw new Error("Agro species list is required.");
        }
        const speciesList = normalizeSpeciesList(payload.speciesList);
        if (!state.currentAccount?.address) {
          return { normalized: [], byYear: {} };
        }
        let balances = await getUnifiedAgroBalances({
          projectContract,
          tokenContract,
          account: state.currentAccount.address,
          projectAddress: payload.contractAddress,
          tokenAddress,
          forceRefresh: true,
        });
        for (const entry of balances) {
          if (!entry.balance || entry.balance.isZero()) {
            continue;
          }
          const status = await tokenContract.redemptionStatus(entry.tokenId);
          if (!status.canRedeemRevenue) {
            continue;
          }
          if (!Number.isInteger(Number(entry.claimYear))) {
            throw new Error("Agro claim balance is missing claimYear.");
          }
          const claimableAmount = await tokenContract.calculatePayout(
            Number(entry.claimYear),
            entry.speciesId,
            entry.balance
          );
          const speciesName = speciesList[Number(entry.speciesId)];
          if (typeof speciesName !== "string") {
            throw new Error("Agro claim balance has invalid species id.");
          }
          const row = {
            year: Number(entry.year),
            species: speciesName,
            speciesId: entry.speciesId,
            amount: parseFloat(
              Number(ethers.utils.formatUnits(entry.balance, 2)).toFixed(2)
            ),
            claimable_usdc: parseFloat(
              Number(ethers.utils.formatUnits(claimableAmount, 6)).toFixed(2)
            ),
            token_amount_raw: entry.balance.toString(),
            token_id: entry.tokenId.toString(),
          };
          normalized.push(row);
          if (!byYear[row.year]) {
            byYear[row.year] = [];
          }
          byYear[row.year].push({
            species: row.species,
            amount: row.amount,
            claimable_usdc: row.claimable_usdc,
            token_id: row.token_id,
          });
        }
        return { normalized, byYear };
      },

    async callGetProvider({ state, commit }) {
      const initProvider = toRaw(state.provider);
      const adapter = toRaw(state.adapter);
      const currentAccount = toRaw(state.currentAccount);

      if (!initProvider) {
        const provider = await getProvider(
          initProvider,
          adapter,
          currentAccount?.type,
          state.networkId,
          currentAccount?.provider
        );
        commit("setProvider", toRaw(provider));
        return provider;
      } else {
        return initProvider;
      }
    },
};

export {
  actions,
  resolveSpeciesId,
  buildUnifiedAgroTokenMeta,
  encodeAgroTokenId,
  SECONDS_PER_YEAR,
};

export default createStore({
  state,
  getters,
  mutations,
  actions,
});
